export type QwenVoiceVisualTopicId =
  | 'speed_limits'
  | 'overtaking'
  | 'right_of_way'
  | 'warning_danger'
  | 'stopping_parking'
  | 'safety_distance'
  | 'mandatory'
  | 'prohibitions'
  | 'information'
  | 'traffic_lights'
  | 'road_markings'
  | 'temporary_signs'
  | 'unknown';

export interface QwenVoiceVisualAsset {
  id: string;
  marker: string;
  topicId: QwenVoiceVisualTopicId;
  labels: string[];
  aliases: string[];
  useCases: string[];
  priority: number;
  variant?: 'primary' | 'alternate';
}

type RegistryAssetInput = Omit<QwenVoiceVisualAsset, 'marker'>;

const asset = (input: RegistryAssetInput): QwenVoiceVisualAsset => ({
  ...input,
  marker: `[[image:sign:${input.id}]]`,
});

export const QWEN_VOICE_VISUAL_ASSETS: QwenVoiceVisualAsset[] = [
  // ── speed_limits (6) ──
  asset({
    id: "Limiti generali di velocità in Italia",
    topicId: 'speed_limits',
    labels: ["Limiti generali di velocità","Limiti per tipo di strada"],
    aliases: ["limiti generali di velocità","limiti di velocità in italia","50 90 110 130","limiti per tipo di strada","velocità per categoria strada"],
    useCases: ["Riassumere i limiti generali di legge (centro abitato, extraurbane, autostrada) e ricordare le riduzioni per pioggia e neopatentati"],
    priority: 1000,
    variant: 'primary',
  }),
  asset({
    id: "Limite massimo di velocità — 80 km/h",
    topicId: 'speed_limits',
    labels: ["Limite massimo 80 km/h","Limite di velocità"],
    aliases: ["limite massimo di velocità","limite 80","velocità massima 80","limite massimo velocità","cerchio rosso con numero"],
    useCases: ["Insegnare il divieto di superare la velocità indicata e distinguerlo dal limite minimo e dalla velocità consigliata"],
    priority: 999,
    variant: 'primary',
  }),
  asset({
    id: "Limite massimo di velocità — tre esempi (60, 80, 100 km/h)",
    topicId: 'speed_limits',
    labels: ["Limite massimo di velocità (tre esempi)","Tre segnali di limite massimo"],
    // "limiti generici" deliberately NOT carried over: the generic limits of the
    // Codice (50/90/110/130) are the ITALIA border panel, a different card.
    aliases: ["tre segnali di limite massimo","60 80 100","tre dischi rossi velocità","esempi di limite massimo"],
    useCases: ["Confrontare tre valori dello stesso segnale di limite massimo e distinguerlo dalla fine del limite, dal limite minimo e dai limiti generici del Codice"],
    priority: 80,
    variant: 'primary',
  }),
  asset({
    id: "Fine del limite massimo di velocità (50 km/h)",
    topicId: 'speed_limits',
    labels: ["Fine limite massimo","Fine del limite 50"],
    aliases: ["fine del limite massimo","fine limite di velocità","cessa il limite","fine limite 50","cerchio sbarrato velocità"],
    useCases: ["Spiegare che cessa solo lo specifico limite e tornano in vigore i limiti generali, distinguendolo da fine di tutti i divieti"],
    priority: 70,
    variant: 'alternate',
  }),
  asset({
    id: "Limite massimo di velocità 80 km/h con pannello di continuazione",
    topicId: 'speed_limits',
    labels: ["Limite 80 con continuazione","Pannello di continuazione"],
    aliases: ["limite con pannello di continuazione","limite 80 continua","frecce contrapposte limite","pannello continuazione velocità","il limite continua"],
    useCases: ["Chiarire che il pannello con frecce contrapposte significa 'il limite continua' e non doppio senso di circolazione"],
    priority: 40,
    variant: 'alternate',
  }),
  asset({
    id: "Limite minimo di velocità — 30 km/h",
    topicId: 'speed_limits',
    labels: ["Limite minimo 30 km/h","Velocità minima"],
    aliases: ["limite minimo di velocità","velocità minima 30","obbligo velocità minima","cerchio blu 30","non scendere sotto"],
    useCases: ["Insegnare l'obbligo di non scendere sotto la velocità indicata e contrapporlo al limite massimo (cerchio rosso) e alla velocità consigliata"],
    priority: 30,
    variant: 'alternate',
  }),
  // ── overtaking (6) ──
  asset({
    id: "Divieto di sorpasso",
    topicId: 'overtaking',
    labels: ["Divieto di sorpasso","No sorpasso"],
    aliases: ["divieto di sorpasso","non si sorpassa","vietato sorpassare","no sorpasso","divieto sorpasso auto"],
    useCases: ["Capire dove non è consentito sorpassare gli altri autoveicoli"],
    priority: 1000,
    variant: 'primary',
  }),
  asset({
    id: "Divieto di sorpasso per veicoli di trasporto cose",
    topicId: 'overtaking',
    labels: ["Divieto di sorpasso per autocarri","No sorpasso camion"],
    aliases: ["divieto di sorpasso per autocarri","non possono sorpassare i camion","divieto sorpasso mezzi pesanti","vietato sorpassare autocarri","divieto sorpasso trasporto cose"],
    useCases: ["Capire quando i mezzi pesanti per trasporto cose non possono sorpassare"],
    priority: 90,
    variant: 'primary',
  }),
  asset({
    id: "Fine del divieto di sorpasso",
    topicId: 'overtaking',
    labels: ["Fine divieto di sorpasso","Si può sorpassare"],
    aliases: ["fine del divieto di sorpasso","fine divieto sorpasso","si può sorpassare di nuovo","cessa il divieto di sorpasso","torna consentito sorpasso"],
    useCases: ["Sapere da dove riprende il permesso di sorpassare"],
    priority: 80,
    variant: 'primary',
  }),
  asset({
    id: "Fine del divieto di sorpasso per autocarri",
    topicId: 'overtaking',
    labels: ["Fine divieto sorpasso autocarri","Camion possono sorpassare"],
    aliases: ["fine del divieto di sorpasso per autocarri","fine divieto sorpasso camion","gli autocarri possono sorpassare","cessa divieto sorpasso mezzi pesanti","fine divieto sorpasso trasporto cose"],
    useCases: ["Sapere da dove gli autocarri possono nuovamente sorpassare"],
    priority: 70,
    variant: 'primary',
  }),
  asset({
    id: "Divieto di sorpasso con pannello integrativo di continuazione",
    topicId: 'overtaking',
    labels: ["Divieto di sorpasso entrambi i sensi","No sorpasso bidirezionale"],
    aliases: ["divieto di sorpasso entrambi i sensi","non si sorpassa in nessun senso","divieto sorpasso doppio senso","frecce contrapposte divieto sorpasso","divieto sorpasso valido in ambo i sensi"],
    useCases: ["Capire che il divieto di sorpasso vale in entrambi i sensi di marcia"],
    priority: 60,
    variant: 'primary',
  }),
  asset({
    id: "Divieto di sorpasso — estensione del tratto",
    topicId: 'overtaking',
    labels: ["Divieto di sorpasso estensione tratto","No sorpasso tratto esteso"],
    aliases: ["divieto di sorpasso estensione del tratto","divieto sorpasso esteso avanti e indietro","frecce verticali divieto sorpasso","estensione del tratto divieto sorpasso","tratto esteso divieto sorpasso"],
    useCases: ["Capire che il divieto di sorpasso comprende anche il punto in cui ci si trova"],
    priority: 50,
    variant: 'primary',
  }),
  // ── right_of_way (15) ──
  asset({
    id: "Dare la precedenza",
    topicId: 'right_of_way',
    labels: ["Dare la precedenza","Triangolo rovesciato"],
    aliases: ["dare la precedenza","dare precedenza","triangolo capovolto","triangolo rovesciato","cedere il passo"],
    useCases: ["Distinguere il dare precedenza (rallentare) dallo STOP (arresto obbligatorio)"],
    priority: 1000,
    variant: 'primary',
  }),
  asset({
    id: "Fermarsi e dare precedenza",
    topicId: 'right_of_way',
    labels: ["STOP","Fermarsi e dare precedenza","Segnale di stop"],
    aliases: ["stop","segnale stop","fermarsi e dare precedenza","ottagono rosso","obbligo di arresto"],
    useCases: ["Capire quando l'arresto completo e' sempre obbligatorio anche senza veicoli in vista"],
    priority: 100,
    variant: 'primary',
  }),
  asset({
    id: "Diritto di precedenza (strada con diritto di precedenza)",
    topicId: 'right_of_way',
    labels: ["Diritto di precedenza","Losanga gialla"],
    aliases: ["diritto di precedenza","strada con diritto di precedenza","losanga gialla","rombo giallo","ho la precedenza"],
    useCases: ["Riconoscere che ho la precedenza sui veicoli che si immettono dalle laterali"],
    priority: 98,
    variant: 'primary',
  }),
  asset({
    id: "Fine del diritto di precedenza",
    topicId: 'right_of_way',
    labels: ["Fine del diritto di precedenza","Losanga sbarrata"],
    aliases: ["fine del diritto di precedenza","fine diritto di precedenza","losanga sbarrata","rombo con barra nera","non ho piu la precedenza"],
    useCases: ["Capire che dopo questo segnale non ho piu la precedenza alle intersezioni"],
    priority: 97,
    variant: 'primary',
  }),
  asset({
    id: "Croce di Sant'Andrea — passaggio a livello senza barriere a due o più binari",
    topicId: 'right_of_way',
    labels: ["Croce di Sant'Andrea doppia","Passaggio a livello senza barriere piu binari"],
    aliases: ["croce di sant'andrea","croce di sant andrea doppia","passaggio a livello senza barriere","doppia x","piu binari"],
    useCases: ["Distinguere la croce doppia (due o piu binari) dalla singola (un solo binario)"],
    priority: 96,
    variant: 'primary',
  }),
  asset({
    id: "Dare precedenza nei sensi unici alternati",
    topicId: 'right_of_way',
    labels: ["Dare precedenza sensi unici alternati","Cerchio rosso strettoia"],
    aliases: ["dare precedenza sensi unici alternati","senso unico alternato dare precedenza","strettoia dare precedenza","cerchio rosso strettoia","ponte stretto"],
    useCases: ["Cedere il passo a chi arriva in senso opposto nella strettoia a senso unico alternato"],
    priority: 95,
    variant: 'primary',
  }),
  asset({
    id: "Diritto di precedenza nei sensi unici alternati",
    topicId: 'right_of_way',
    labels: ["Diritto di precedenza sensi unici alternati","Quadrato blu strettoia"],
    aliases: ["diritto di precedenza sensi unici alternati","senso unico alternato ho precedenza","strettoia ho precedenza","quadrato blu strettoia"],
    useCases: ["Sapere che nella strettoia a senso unico alternato passo io per primo"],
    priority: 94,
    variant: 'primary',
  }),
  asset({
    id: "Preavviso di STOP a 320 metri",
    topicId: 'right_of_way',
    labels: ["Preavviso di STOP","STOP a 320 m"],
    aliases: ["preavviso di stop","stop a 320 metri","preavviso stop","pannello distanziometrico stop"],
    useCases: ["Capire che lo STOB vero e' a 320 m, non immediatamente al cartello di preavviso"],
    priority: 80,
    variant: 'alternate',
  }),
  asset({
    id: "Dare precedenza con preavviso a 320 m",
    topicId: 'right_of_way',
    labels: ["Preavviso dare precedenza","Dare precedenza a 320 m"],
    aliases: ["dare precedenza con preavviso","preavviso dare precedenza 320 m","preavviso di dare precedenza","pannello distanziometrico precedenza"],
    useCases: ["Distinguere il preavviso a distanza dall'obbligo immediato di dare precedenza"],
    priority: 79,
    variant: 'alternate',
  }),
  asset({
    id: "Confronto: diritto di precedenza (A) e direzione della strada con diritto di precedenza (B)",
    topicId: 'right_of_way',
    labels: ["Confronto diritto di precedenza e direzione","Pannello A e B"],
    aliases: ["confronto diritto di precedenza","diritto di precedenza e direzione","pannello losanga e direzione","direzione strada con diritto di precedenza"],
    useCases: ["Capire come la strada con diritto di precedenza si articola all'intersezione"],
    priority: 78,
    variant: 'alternate',
  }),
  asset({
    id: "Schema didattico: croce di Sant'Andrea (passaggio a livello) e segnale STOP",
    topicId: 'right_of_way',
    labels: ["Schema croce di Sant'Andrea e STOP","Confronto croce e stop"],
    aliases: ["schema croce di sant'andrea e stop","confronto croce di sant andrea stop","passaggio a livello e stop","quando arrestarsi croce"],
    useCases: ["Distinguere quando la croce impone l'arresto rispetto allo STOP"],
    priority: 77,
    variant: 'alternate',
  }),
  asset({
    id: "Schema didattico — passaggio a livello con barriere e relativa croce di Sant'Andrea",
    topicId: 'right_of_way',
    labels: ["Schema passaggio a livello con barriere","Croce e preavviso passaggio a livello"],
    aliases: ["schema passaggio a livello con barriere","croce di sant andrea con barriere","passaggio a livello con barriere","preavviso passaggio a livello"],
    useCases: ["Riconoscere la coppia croce + preavviso al passaggio a livello con barriere"],
    priority: 76,
    variant: 'alternate',
  }),
  asset({
    id: "Schema di precedenza in incrocio con tram, autocarro e autovettura",
    topicId: 'right_of_way',
    labels: ["Incrocio con tram","Precedenza tram autocarro auto"],
    aliases: ["incrocio con tram","precedenza tram","schema incrocio tram autocarro","chi passa prima con il tram"],
    useCases: ["Ricordare la priorita' assoluta del tram sul suo binario di marcia"],
    priority: 75,
    variant: 'alternate',
  }),
  asset({
    id: "Schema di precedenza in incrocio con segnali di 'dare la precedenza'",
    topicId: 'right_of_way',
    labels: ["Incrocio con dare la precedenza","Schema incrocio segnali precedenza"],
    aliases: ["incrocio con segnali dare la precedenza","schema incrocio dare precedenza","incrocio con segnali di precedenza","chi passa prima con i segnali"],
    useCases: ["Capire che i segnali derogano alla regola della precedenza a destra"],
    priority: 74,
    variant: 'alternate',
  }),
  asset({
    id: "Schema di precedenza in incrocio non regolato (tre veicoli)",
    topicId: 'right_of_way',
    labels: ["Incrocio non regolato tre veicoli","Precedenza a destra"],
    aliases: ["incrocio non regolato","precedenza a destra","schema incrocio tre veicoli","chi passa prima senza segnali"],
    useCases: ["Applicare la regola della precedenza a destra in un incrocio senza segnali"],
    priority: 73,
    variant: 'alternate',
  }),
  // ── warning_danger (65) ──
  asset({
    id: "Curva pericolosa a destra",
    topicId: 'warning_danger',
    labels: ["Curva pericolosa a destra","Curva a destra"],
    aliases: ["curva pericolosa a destra","curva a destra","curva verso destra"],
    useCases: ["Riconoscere il preavviso di curva a destra e distinguerlo dalla speculare a sinistra"],
    priority: 1000,
    variant: 'primary',
  }),
  asset({
    id: "Curva pericolosa a sinistra",
    topicId: 'warning_danger',
    labels: ["Curva pericolosa a sinistra","Curva a sinistra"],
    aliases: ["curva pericolosa a sinistra","curva a sinistra","curva verso sinistra"],
    useCases: ["Riconoscere il preavviso di curva a sinistra e distinguerlo dalla speculare a destra"],
    priority: 99,
    variant: 'primary',
  }),
  asset({
    id: "Dosso",
    topicId: 'warning_danger',
    labels: ["Dosso"],
    aliases: ["dosso","sporgenza della strada","cunetta convessa","gobba"],
    useCases: ["Distinguere il dosso (convesso) dalla cunetta (concava) e ricordare il divieto di sorpasso"],
    priority: 98,
    variant: 'primary',
  }),
  asset({
    id: "Cunetta",
    topicId: 'warning_danger',
    labels: ["Cunetta"],
    aliases: ["cunetta","avvallamento","cunetta concava","buca trasversale"],
    useCases: ["Distinguere la cunetta (avvallamento concavo) dal dosso (convesso)"],
    priority: 97,
    variant: 'primary',
  }),
  asset({
    id: "Strettoia simmetrica (restringimento da entrambi i lati)",
    topicId: 'warning_danger',
    labels: ["Strettoia simmetrica","Restringimento simmetrico"],
    aliases: ["strettoia simmetrica","restringimento da entrambi i lati","strada che si restringe","strettoia su due lati"],
    useCases: ["Distinguere la strettoia simmetrica dalle versioni asimmetriche e dal doppio senso"],
    priority: 96,
    variant: 'primary',
  }),
  asset({
    id: "Attraversamento pedonale (preavviso)",
    topicId: 'warning_danger',
    labels: ["Attraversamento pedonale","Preavviso pedoni"],
    aliases: ["attraversamento pedonale","preavviso pedoni","triangolo con pedone","strisce pedonali pericolo"],
    useCases: ["Distinguere il triangolo di preavviso pedoni dal quadrato blu di indicazione"],
    priority: 95,
    variant: 'primary',
  }),
  asset({
    id: "Passaggio a livello con barriere o semibarriere",
    topicId: 'warning_danger',
    labels: ["Passaggio a livello con barriere","Con barriere"],
    aliases: ["passaggio a livello con barriere","passaggio a livello con semibarriere","staccionata","triangolo con barriera"],
    useCases: ["Distinguere il passaggio a livello con barriere (staccionata) da quello senza (locomotiva)"],
    priority: 94,
    variant: 'primary',
  }),
  asset({
    id: "Passaggio a livello senza barriere",
    topicId: 'warning_danger',
    labels: ["Passaggio a livello senza barriere","Senza barriere"],
    aliases: ["passaggio a livello senza barriere","triangolo con locomotiva","treno pericolo","passaggio non protetto"],
    useCases: ["Distinguere il passaggio a livello senza barriere (locomotiva) da quello con barriere"],
    priority: 93,
    variant: 'primary',
  }),
  asset({
    id: "Croce di Sant'Andrea (passaggio a livello a un binario)",
    topicId: 'warning_danger',
    labels: ["Croce di Sant'Andrea","Un binario"],
    aliases: ["croce di sant'andrea","croce di santandrea","passaggio a livello un binario","croce a una x"],
    useCases: ["Distinguere la croce a un binario (una X) da quella a doppio binario"],
    priority: 92,
    variant: 'primary',
  }),
  asset({
    id: "Strada sdrucciolevole",
    topicId: 'warning_danger',
    labels: ["Strada sdrucciolevole","Fondo scivoloso"],
    aliases: ["strada sdrucciolevole","fondo scivoloso","strada scivolosa","sbandamento"],
    useCases: ["Riconoscere il pericolo generico di scivolosità e distinguerlo da strada bagnata"],
    priority: 91,
    variant: 'primary',
  }),
  asset({
    id: "Discesa pericolosa al 10%",
    topicId: 'warning_danger',
    labels: ["Discesa pericolosa","Discesa al 10%"],
    aliases: ["discesa pericolosa","discesa al 10","strada in discesa","pendenza in discesa"],
    useCases: ["Distinguere la discesa (rampa che scende verso destra) dalla salita ripida"],
    priority: 90,
    variant: 'primary',
  }),
  asset({
    id: "Salita ripida (pendenza 10%)",
    topicId: 'warning_danger',
    labels: ["Salita ripida","Salita al 10%"],
    aliases: ["salita ripida","salita al 10","strada in salita","pendenza in salita"],
    useCases: ["Distinguere la salita (rampa che sale verso destra) dalla discesa pericolosa"],
    priority: 89,
    variant: 'primary',
  }),
  asset({
    id: "Semaforo verticale (preavviso di impianto semaforico)",
    topicId: 'warning_danger',
    labels: ["Semaforo (preavviso)","Semaforo verticale"],
    aliases: ["semaforo","preavviso semaforo","impianto semaforico","triangolo con semaforo"],
    useCases: ["Distinguere il triangolo di preavviso semaforo dal semaforo reale"],
    priority: 88,
    variant: 'primary',
  }),
  asset({
    id: "Attraversamento pedonale di bambini",
    topicId: 'warning_danger',
    labels: ["Bambini","Attraversamento bambini"],
    aliases: ["bambini","attraversamento bambini","scuola","pedone bambino"],
    useCases: ["Distinguere il pericolo bambini dall'attraversamento pedonale generico"],
    priority: 87,
    variant: 'primary',
  }),
  asset({
    id: "Caduta massi",
    topicId: 'warning_danger',
    labels: ["Caduta massi"],
    aliases: ["caduta massi","caduta sassi","frana","massi sulla strada"],
    useCases: ["Riconoscere il pericolo di caduta massi su strade di montagna"],
    priority: 86,
    variant: 'primary',
  }),
  asset({
    id: "Animali selvatici vaganti",
    topicId: 'warning_danger',
    labels: ["Animali selvatici vaganti","Animali selvatici"],
    aliases: ["animali selvatici","animali selvatici vaganti","cervo","fauna selvatica"],
    useCases: ["Distinguere gli animali selvatici (cervo) dagli animali domestici (mucca)"],
    priority: 85,
    variant: 'primary',
  }),
  asset({
    id: "Animali domestici vaganti",
    topicId: 'warning_danger',
    labels: ["Animali domestici vaganti","Animali domestici"],
    aliases: ["animali domestici","animali domestici vaganti","mucca","bovini sulla strada"],
    useCases: ["Distinguere gli animali domestici (mucca) dagli animali selvatici (cervo)"],
    priority: 84,
    variant: 'primary',
  }),
  asset({
    id: "Doppia curva, la prima a destra",
    topicId: 'warning_danger',
    labels: ["Doppia curva, prima a destra"],
    aliases: ["doppia curva a destra","doppia curva prima a destra","due curve a destra","serie di curve a destra"],
    useCases: ["Riconoscere la doppia curva con prima svolta a destra"],
    priority: 83,
    variant: 'primary',
  }),
  asset({
    id: "Doppia curva, la prima a sinistra",
    topicId: 'warning_danger',
    labels: ["Doppia curva, prima a sinistra"],
    aliases: ["doppia curva a sinistra","doppia curva prima a sinistra","due curve a sinistra","serie di curve a sinistra"],
    useCases: ["Riconoscere la doppia curva con prima svolta a sinistra"],
    priority: 82,
    variant: 'primary',
  }),
  asset({
    id: "Strada deformata",
    topicId: 'warning_danger',
    labels: ["Strada deformata"],
    aliases: ["strada deformata","fondo dissestato","strada sconnessa","buche"],
    useCases: ["Riconoscere il pericolo di pavimentazione deformata o dissestata"],
    priority: 81,
    variant: 'primary',
  }),
  asset({
    id: "Strettoia asimmetrica a destra",
    topicId: 'warning_danger',
    labels: ["Strettoia asimmetrica a destra"],
    aliases: ["strettoia asimmetrica a destra","restringimento a destra","strada che si restringe a destra"],
    useCases: ["Distinguere la strettoia asimmetrica a destra da quella simmetrica e a sinistra"],
    priority: 80,
    variant: 'primary',
  }),
  asset({
    id: "Strettoia asimmetrica — restringimento a sinistra",
    topicId: 'warning_danger',
    labels: ["Strettoia asimmetrica a sinistra"],
    aliases: ["strettoia asimmetrica a sinistra","restringimento a sinistra","strada che si restringe a sinistra"],
    useCases: ["Distinguere la strettoia asimmetrica a sinistra da quella simmetrica e a destra"],
    priority: 79,
    variant: 'primary',
  }),
  asset({
    id: "Doppio senso di circolazione",
    topicId: 'warning_danger',
    labels: ["Doppio senso di circolazione"],
    aliases: ["doppio senso","doppio senso di circolazione","frecce contrapposte","ritorno al doppio senso"],
    useCases: ["Distinguere il doppio senso (frecce contrapposte) dalla strettoia"],
    priority: 78,
    variant: 'primary',
  }),
  asset({
    id: "Intersezione con precedenza a destra",
    topicId: 'warning_danger',
    labels: ["Intersezione con precedenza a destra"],
    aliases: ["precedenza a destra","intersezione con precedenza a destra","incrocio precedenza a destra","croce di precedenza"],
    useCases: ["Riconoscere l'incrocio dove vige la precedenza a destra"],
    priority: 77,
    variant: 'primary',
  }),
  asset({
    id: "Intersezione con strada laterale a destra",
    topicId: 'warning_danger',
    labels: ["Intersezione con strada laterale a destra"],
    aliases: ["intersezione strada laterale a destra","strada laterale a destra","incrocio a destra"],
    useCases: ["Riconoscere l'intersezione con strada laterale entrante da destra"],
    priority: 76,
    variant: 'primary',
  }),
  asset({
    id: "Intersezione con strada di minore importanza da sinistra",
    topicId: 'warning_danger',
    labels: ["Strada di minore importanza da sinistra"],
    aliases: ["strada di minore importanza da sinistra","intersezione minore da sinistra","strada secondaria a sinistra"],
    useCases: ["Riconoscere l'intersezione con strada di minore importanza entrante da sinistra"],
    priority: 75,
    variant: 'primary',
  }),
  asset({
    id: "Intersezione con strada di minore importanza (con diritto di precedenza)",
    topicId: 'warning_danger',
    labels: ["Strada di minore importanza (con precedenza)"],
    aliases: ["strada di minore importanza","intersezione con diritto di precedenza","strada secondaria","intersezione strada minore"],
    useCases: ["Riconoscere l'intersezione dove si ha diritto di precedenza sulla strada minore"],
    priority: 74,
    variant: 'primary',
  }),
  asset({
    id: "Rotatoria (preavviso)",
    topicId: 'warning_danger',
    labels: ["Rotatoria","Preavviso rotatoria"],
    aliases: ["rotatoria","preavviso rotatoria","rotonda","frecce circolari"],
    useCases: ["Riconoscere il preavviso di rotatoria"],
    priority: 73,
    variant: 'primary',
  }),
  asset({
    id: "Lavori",
    topicId: 'warning_danger',
    labels: ["Lavori","Lavori in corso"],
    aliases: ["lavori","lavori in corso","cantiere","omino che scava"],
    useCases: ["Riconoscere il pericolo di lavori in corso / cantiere"],
    priority: 72,
    variant: 'primary',
  }),
  asset({
    id: "Altri pericoli",
    topicId: 'warning_danger',
    labels: ["Altri pericoli"],
    aliases: ["altri pericoli","pericolo generico","punto esclamativo","triangolo con punto esclamativo"],
    useCases: ["Riconoscere il segnale generico di altri pericoli (punto esclamativo)"],
    priority: 71,
    variant: 'primary',
  }),
  asset({
    id: "Confluenza a sinistra",
    topicId: 'warning_danger',
    labels: ["Confluenza a sinistra"],
    aliases: ["confluenza a sinistra","strada che confluisce da sinistra","immissione da sinistra"],
    useCases: ["Riconoscere la confluenza di una strada entrante da sinistra"],
    priority: 60,
    variant: 'alternate',
  }),
  asset({
    id: "Confluenza con corsia di accelerazione",
    topicId: 'warning_danger',
    labels: ["Confluenza con corsia di accelerazione"],
    aliases: ["confluenza con corsia di accelerazione","corsia di accelerazione","immissione con corsia"],
    useCases: ["Riconoscere la confluenza con corsia di accelerazione"],
    priority: 59,
    variant: 'alternate',
  }),
  asset({
    id: "Vento laterale",
    topicId: 'warning_danger',
    labels: ["Vento laterale"],
    aliases: ["vento laterale","vento forte","manica a vento","raffiche laterali"],
    useCases: ["Riconoscere il pericolo di vento laterale (manica a vento)"],
    priority: 58,
    variant: 'alternate',
  }),
  asset({
    id: "Sbocco su molo o su argine",
    topicId: 'warning_danger',
    labels: ["Sbocco su molo o su argine"],
    // The banchina-pericolosa vocabulary is dropped: that is a DIFFERENT sign
    // (soft shoulder, no water) and these aliases used to attach this picture.
    aliases: ["sbocco su molo","sbocco su argine","strada che finisce in acqua","molo","argine"],
    useCases: ["Riconoscere il pericolo di finire in acqua dove la strada sbocca su un molo o su un argine","Distinguerlo da banchina pericolosa"],
    priority: 57,
    variant: 'alternate',
  }),
  asset({
    id: "Banchina cedevole o pavimentazione sconnessa al margine",
    topicId: 'warning_danger',
    labels: ["Banchina cedevole"],
    aliases: ["banchina cedevole","margine cedevole","pavimentazione sconnessa al margine"],
    useCases: ["Riconoscere il pericolo di banchina cedevole al margine della strada"],
    priority: 56,
    variant: 'alternate',
  }),
  asset({
    id: "Materiale instabile sulla strada (proiezione di pietrisco)",
    topicId: 'warning_danger',
    labels: ["Materiale instabile / pietrisco"],
    aliases: ["materiale instabile","proiezione di pietrisco","ghiaia sulla strada","pietre sollevate"],
    useCases: ["Riconoscere il pericolo di proiezione di pietrisco / materiale instabile"],
    priority: 55,
    variant: 'alternate',
  }),
  asset({
    id: "Ponte mobile",
    topicId: 'warning_danger',
    labels: ["Ponte mobile"],
    aliases: ["ponte mobile","ponte levatoio","ponte apribile"],
    useCases: ["Riconoscere il pericolo di ponte mobile"],
    priority: 54,
    variant: 'alternate',
  }),
  asset({
    id: "Pericolo di incendio",
    topicId: 'warning_danger',
    labels: ["Pericolo di incendio"],
    aliases: ["pericolo di incendio","incendio","fiamma","rischio incendio"],
    useCases: ["Riconoscere il pericolo di incendio"],
    priority: 53,
    variant: 'alternate',
  }),
  asset({
    id: "Aeromobili a bassa quota",
    topicId: 'warning_danger',
    labels: ["Aeromobili a bassa quota"],
    aliases: ["aeromobili a bassa quota","aerei in volo basso","aeroplano","bassa quota"],
    useCases: ["Riconoscere il pericolo di aeromobili a bassa quota"],
    priority: 52,
    variant: 'alternate',
  }),
  asset({
    id: "Attraversamento ciclabile o presenza di ciclisti",
    topicId: 'warning_danger',
    labels: ["Attraversamento ciclabile / ciclisti"],
    aliases: ["attraversamento ciclabile","presenza di ciclisti","ciclisti","bicicletta pericolo"],
    useCases: ["Riconoscere il pericolo di attraversamento ciclabile o presenza di ciclisti"],
    priority: 51,
    variant: 'alternate',
  }),
  asset({
    id: "Attraversamento tranviario",
    topicId: 'warning_danger',
    labels: ["Attraversamento tranviario"],
    aliases: ["attraversamento tranviario","tram","binari del tram","tranvia"],
    useCases: ["Riconoscere il pericolo di attraversamento tranviario (tram)"],
    priority: 50,
    variant: 'alternate',
  }),
  asset({
    id: "Caduta di massi (dalla destra)",
    topicId: 'warning_danger',
    labels: ["Caduta massi (dalla destra)"],
    aliases: ["caduta massi dalla destra","frana da destra","massi da destra"],
    useCases: ["Variante della caduta massi con pericolo proveniente da destra"],
    priority: 49,
    variant: 'alternate',
  }),
  asset({
    id: "Strada sdrucciolevole — pericolo da gelo o neve",
    topicId: 'warning_danger',
    labels: ["Sdrucciolevole — gelo o neve"],
    aliases: ["strada sdrucciolevole per gelo","ghiaccio","neve scivolosa","fondo gelato"],
    useCases: ["Variante della strada sdrucciolevole per gelo o neve"],
    priority: 48,
    variant: 'alternate',
  }),
  asset({
    id: "Strada sdrucciolevole con neve o ghiaccio",
    topicId: 'warning_danger',
    labels: ["Sdrucciolevole con neve o ghiaccio"],
    aliases: ["sdrucciolevole con neve","ghiaccio sulla strada","neve e ghiaccio"],
    useCases: ["Variante della strada sdrucciolevole con neve o ghiaccio"],
    priority: 47,
    variant: 'alternate',
  }),
  asset({
    id: "Strada sdrucciolevole — direzione del pericolo a destra",
    topicId: 'warning_danger',
    labels: ["Sdrucciolevole — direzione a destra"],
    aliases: ["sdrucciolevole direzione a destra","sbandamento a destra","scivolosa verso destra"],
    useCases: ["Variante direzionale della strada sdrucciolevole (pericolo a destra)"],
    priority: 46,
    variant: 'alternate',
  }),
  asset({
    id: "Strada sdrucciolevole con pannello integrativo di pioggia",
    topicId: 'warning_danger',
    labels: ["Sdrucciolevole con pioggia"],
    aliases: ["sdrucciolevole con pioggia","strada scivolosa con pioggia","pannello pioggia"],
    useCases: ["Variante della strada sdrucciolevole con pannello di pioggia"],
    priority: 45,
    variant: 'alternate',
  }),
  asset({
    id: "Cunetta con allagamento in caso di pioggia",
    topicId: 'warning_danger',
    labels: ["Cunetta con allagamento"],
    aliases: ["cunetta con allagamento","cunetta allagata","avvallamento con acqua"],
    useCases: ["Variante della cunetta con allagamento in caso di pioggia"],
    priority: 44,
    variant: 'alternate',
  }),
  asset({
    id: "Salita ripida (pendenza 10%) con pannello «autocarri in rallentamento»",
    topicId: 'warning_danger',
    labels: ["Salita ripida con pannello veicoli pesanti"],
    aliases: ["salita con pannello veicoli pesanti","salita tamponamento camion","salita pesanti"],
    useCases: ["Variante della salita ripida con pannello per veicoli pesanti"],
    priority: 43,
    variant: 'alternate',
  }),
  asset({
    id: "Doppia curva, la prima a destra — estesa per 380 m",
    topicId: 'warning_danger',
    labels: ["Doppia curva a destra — 380 m"],
    aliases: ["doppia curva a destra 380 metri","doppia curva estesa 380","curve per 380 m"],
    useCases: ["Variante della doppia curva a destra con estensione 380 m"],
    priority: 42,
    variant: 'alternate',
  }),
  asset({
    id: "Doppia curva, la prima a destra — estesa per 3,8 km",
    topicId: 'warning_danger',
    labels: ["Doppia curva a destra — 3,8 km"],
    aliases: ["doppia curva a destra 3,8 km","doppia curva estesa 3.8 km","curve per 3,8 chilometri"],
    useCases: ["Variante della doppia curva a destra con estensione 3,8 km"],
    priority: 41,
    variant: 'alternate',
  }),
  asset({
    id: "Doppia curva, la prima a destra — estensione 2,8 km",
    topicId: 'warning_danger',
    labels: ["Doppia curva a destra — 2,8 km"],
    aliases: ["doppia curva a destra 2,8 km","doppia curva estesa 2.8 km","curve per 2,8 chilometri"],
    useCases: ["Variante della doppia curva a destra con estensione 2,8 km"],
    priority: 40,
    variant: 'alternate',
  }),
  asset({
    id: "Strada deformata (per 320 metri)",
    topicId: 'warning_danger',
    labels: ["Strada deformata — 320 m"],
    aliases: ["strada deformata 320 metri","strada dissestata 320 m","deformata per 320"],
    useCases: ["Variante della strada deformata con estensione 320 m"],
    priority: 39,
    variant: 'alternate',
  }),
  asset({
    id: "Strada deformata (con indicazione di estensione 380 m)",
    topicId: 'warning_danger',
    labels: ["Strada deformata — 380 m"],
    aliases: ["strada deformata 380 metri","strada dissestata 380 m","deformata per 380"],
    useCases: ["Variante della strada deformata con estensione 380 m"],
    priority: 38,
    variant: 'alternate',
  }),
  asset({
    id: "Strada deformata con pannello integrativo di fine",
    topicId: 'warning_danger',
    labels: ["Strada deformata con pannello estensione"],
    aliases: ["strada deformata con pannello","deformata tratto pericoloso","pannello estensione deformata"],
    useCases: ["Variante della strada deformata con pannello di estensione del tratto"],
    priority: 37,
    variant: 'alternate',
  }),
  asset({
    id: "Pannelli distanziometrici di passaggio a livello con barriere",
    topicId: 'warning_danger',
    labels: ["Pannelli distanziometrici passaggio a livello"],
    aliases: ["pannelli distanziometrici","distanziometrici passaggio a livello","barre oblique passaggio a livello"],
    useCases: ["Riconoscere i pannelli distanziometrici che precedono il passaggio a livello"],
    priority: 36,
    variant: 'alternate',
  }),
  asset({
    id: "Semaforo orizzontale (preavviso di impianto semaforico orizzontale)",
    topicId: 'warning_danger',
    labels: ["Semaforo orizzontale (preavviso)"],
    aliases: ["semaforo orizzontale","preavviso semaforo orizzontale","luci orizzontali"],
    useCases: ["Variante del preavviso di impianto semaforico orizzontale"],
    priority: 35,
    variant: 'alternate',
  }),
  asset({
    id: "Semaforo (segnale temporaneo)",
    topicId: 'warning_danger',
    labels: ["Semaforo (temporaneo)"],
    aliases: ["semaforo temporaneo","semaforo di cantiere","preavviso semaforo cantiere"],
    useCases: ["Variante temporanea di cantiere del preavviso di semaforo"],
    priority: 34,
    variant: 'alternate',
  }),
  asset({
    id: "SCUOLABUS",
    topicId: 'warning_danger',
    labels: ["SCUOLABUS"],
    // NOT "bambini cantiere": this is the orange panel carried ON the school bus,
    // not the roadside danger sign, and those aliases used to attach it there.
    aliases: ["scuolabus","pannello scuolabus","trasporto scolastico","bambini a bordo"],
    useCases: ["Riconoscere il pannello SCUOLABUS esposto dal veicolo","Distinguerlo dal segnale di pericolo Bambini posto a bordo strada"],
    priority: 33,
    variant: 'alternate',
  }),
  asset({
    id: "Lavori in corso — segnale temporaneo con pannello integrativo di fine",
    topicId: 'warning_danger',
    labels: ["Lavori in corso (temporaneo)"],
    aliases: ["lavori in corso temporaneo","cantiere temporaneo","lavori presenza in coincidenza"],
    useCases: ["Variante temporanea del segnale di lavori in corso"],
    priority: 32,
    variant: 'alternate',
  }),
  asset({
    id: "Barriera direzionale di deviazione provvisoria a sinistra",
    topicId: 'warning_danger',
    labels: ["Barriera direzionale deviazione a sinistra"],
    aliases: ["barriera direzionale a sinistra","deviazione provvisoria a sinistra","barriera di deviazione"],
    useCases: ["Riconoscere la barriera direzionale di deviazione provvisoria a sinistra"],
    priority: 31,
    variant: 'alternate',
  }),
  asset({
    id: "Altri pericoli — attraversamento di binari di manovra (manovre ferroviarie)",
    topicId: 'warning_danger',
    labels: ["Altri pericoli — binari di manovra"],
    aliases: ["binari di manovra","manovre ferroviarie","altri pericoli binari"],
    useCases: ["Variante di altri pericoli per attraversamento di binari di manovra"],
    priority: 30,
    variant: 'alternate',
  }),
  asset({
    id: "Altri pericoli abbinato a pannello — sgombraneve in attività",
    topicId: 'warning_danger',
    labels: ["Altri pericoli — sgombraneve"],
    aliases: ["sgombraneve","altri pericoli sgombraneve","spazzaneve in attività"],
    useCases: ["Variante di altri pericoli con pannello sgombraneve in attività"],
    priority: 29,
    variant: 'alternate',
  }),
  asset({
    id: "Altri pericoli con pannello integrativo «mezzi di lavoro in azione»",
    topicId: 'warning_danger',
    labels: ["Altri pericoli — veicoli militari"],
    aliases: ["veicoli militari in manovra","altri pericoli militari","mezzi militari"],
    useCases: ["Variante di altri pericoli con pannello veicoli militari in manovra"],
    priority: 28,
    variant: 'alternate',
  }),
  asset({
    id: "Altri pericoli con pannello integrativo (binari di manovra industriali)",
    topicId: 'warning_danger',
    labels: ["Altri pericoli — binari industriali"],
    aliases: ["binari di manovra industriali","altri pericoli binari industriali","raccordo industriale"],
    useCases: ["Variante di altri pericoli con pannello binari di manovra industriali"],
    priority: 27,
    variant: 'alternate',
  }),
  asset({
    id: "Altri pericoli con pannello integrativo (formazione di ghiaccio)",
    topicId: 'warning_danger',
    labels: ["Altri pericoli — formazione di ghiaccio"],
    aliases: ["formazione di ghiaccio","altri pericoli ghiaccio","pericolo ghiaccio pannello"],
    useCases: ["Variante di altri pericoli con pannello formazione di ghiaccio"],
    priority: 26,
    variant: 'alternate',
  }),
  // ── stopping_parking (23) ──
  asset({
    id: "Divieto di sosta",
    topicId: 'stopping_parking',
    labels: ["Divieto di sosta","Sosta vietata"],
    aliases: ["divieto di sosta","sosta vietata","non si sosta","vietato sostare","non si parcheggia"],
    useCases: ["Studente vuole il segnale base che vieta la sosta ma permette la fermata breve"],
    priority: 1000,
    variant: 'primary',
  }),
  asset({
    id: "Divieto di fermata",
    topicId: 'stopping_parking',
    labels: ["Divieto di fermata","Fermata vietata"],
    aliases: ["divieto di fermata","fermata vietata","non ci si ferma","vietato fermarsi","doppia barra a x"],
    useCases: ["Studente vuole il segnale piu severo con la croce a X che vieta sosta e fermata"],
    priority: 99,
    variant: 'primary',
  }),
  asset({
    id: "Divieto di sosta con rimozione forzata",
    topicId: 'stopping_parking',
    labels: ["Divieto di sosta con rimozione forzata","Sosta vietata rimozione"],
    aliases: ["rimozione forzata","carro attrezzi","divieto sosta rimozione","ti portano via la macchina"],
    useCases: ["Studente vuole il divieto di sosta col pannello carro attrezzi che avverte della rimozione"],
    priority: 98,
    variant: 'primary',
  }),
  asset({
    id: "Divieto di sosta con rimozione coatta (zona rimozione)",
    topicId: 'stopping_parking',
    labels: ["Divieto di sosta zona rimozione","Zona rimozione"],
    aliases: ["zona rimozione","rimozione coatta","divieto sosta zona rimozione"],
    useCases: ["Studente vuole il divieto di sosta col pannello zona rimozione coatta"],
    priority: 97,
    variant: 'primary',
  }),
  asset({
    id: "Divieto di sosta e di fermata (con pannello inizio del divieto)",
    topicId: 'stopping_parking',
    labels: ["Divieto di sosta e fermata","Sosta e fermata vietate"],
    aliases: ["divieto di sosta e fermata","sosta e fermata vietate","vietato sostare e fermarsi","inizio del divieto"],
    useCases: ["Studente vuole il segnale combinato che vieta sia sosta sia fermata col pannello di inizio"],
    priority: 96,
    variant: 'primary',
  }),
  asset({
    id: "Divieto di sosta — eccetto parcheggio per disabili",
    topicId: 'stopping_parking',
    labels: ["Divieto di sosta eccetto disabili","Sosta riservata disabili"],
    aliases: ["sosta eccetto disabili","parcheggio disabili","divieto sosta tranne invalidi","riservato disabili"],
    useCases: ["Studente vuole il divieto di sosta con eccezione per i veicoli al servizio dei disabili"],
    priority: 95,
    variant: 'primary',
  }),
  asset({
    id: "Divieto di sosta nei giorni festivi",
    topicId: 'stopping_parking',
    labels: ["Divieto di sosta giorni festivi","Sosta vietata festivi"],
    aliases: ["divieto sosta festivi","sosta vietata giorni festivi","croce giorni festivi","non si sosta la domenica"],
    useCases: ["Studente vuole il divieto di sosta limitato ai soli giorni festivi"],
    priority: 80,
    variant: 'alternate',
  }),
  asset({
    id: "Divieto di sosta nei giorni festivi 8,00-20,00",
    topicId: 'stopping_parking',
    labels: ["Divieto di sosta festivi 8-20","Sosta vietata festivi orario"],
    aliases: ["divieto sosta festivi 8 20","sosta vietata festivi orario","festivi otto venti"],
    useCases: ["Studente vuole il divieto di sosta nei festivi con fascia oraria 8-20 esplicita"],
    priority: 79,
    variant: 'alternate',
  }),
  asset({
    id: "Divieto di sosta a tempo determinato",
    topicId: 'stopping_parking',
    labels: ["Divieto di sosta a tempo determinato","Sosta vietata a orario"],
    aliases: ["divieto sosta a tempo","sosta vietata orario","divieto sosta a tempo determinato","fascia oraria sosta"],
    useCases: ["Studente vuole il divieto di sosta valido tutti i giorni in una fascia oraria fissa"],
    priority: 78,
    variant: 'alternate',
  }),
  asset({
    id: "Divieto di sosta (con limitazione oraria nei giorni lavorativi)",
    topicId: 'stopping_parking',
    labels: ["Divieto di sosta giorni lavorativi","Sosta vietata feriali"],
    aliases: ["divieto sosta giorni lavorativi","martelli incrociati","sosta vietata feriali","giorni lavorativi sosta"],
    useCases: ["Studente vuole il divieto di sosta limitato ai giorni lavorativi con i martelli incrociati"],
    priority: 77,
    variant: 'alternate',
  }),
  asset({
    id: "Divieto di sosta con pannello integrativo per giorni lavorativi",
    topicId: 'stopping_parking',
    labels: ["Divieto di sosta lavorativi 8-20","Sosta vietata lavorativi orario"],
    aliases: ["divieto sosta lavorativi orario","martelli incrociati 8 20","sosta vietata giorni lavorativi orario"],
    useCases: ["Studente vuole il divieto di sosta nei giorni lavorativi 8-20 col pannello martelli"],
    priority: 76,
    variant: 'alternate',
  }),
  asset({
    id: "Divieto di sosta nelle ore di pulizia stradale (lun-ven 21:00-4:00)",
    topicId: 'stopping_parking',
    labels: ["Divieto di sosta pulizia stradale","Sosta vietata pulizia"],
    aliases: ["divieto sosta pulizia stradale","autospazzatrice","pulizia strada di notte","lavaggio strada"],
    useCases: ["Studente vuole il divieto di sosta per la pulizia stradale notturna lun-ven"],
    priority: 75,
    variant: 'alternate',
  }),
  asset({
    id: "Divieto di sosta a fasce orarie alternate con parcheggio consentito",
    topicId: 'stopping_parking',
    labels: ["Divieto di sosta a fasce alternate","Sosta alternata"],
    aliases: ["divieto sosta fasce alternate","sosta alternata orari","parcheggio a fasce orarie"],
    useCases: ["Studente vuole il segnale con alternanza tra divieto di sosta e parcheggio consentito"],
    priority: 74,
    variant: 'alternate',
  }),
  asset({
    id: "Divieto di sosta agli autoarticolati",
    topicId: 'stopping_parking',
    labels: ["Divieto di sosta autoarticolati","Sosta vietata autoarticolati"],
    aliases: ["divieto sosta autoarticolati","sosta vietata camion articolati","autoarticolati sosta"],
    useCases: ["Studente vuole il divieto di sosta riservato ai soli autoarticolati"],
    priority: 73,
    variant: 'alternate',
  }),
  asset({
    id: "Divieto di sosta con eccezione per autobus",
    topicId: 'stopping_parking',
    labels: ["Divieto di sosta eccetto autobus","Sosta riservata autobus"],
    aliases: ["divieto sosta eccetto autobus","sosta riservata bus","eccetto autobus"],
    useCases: ["Studente vuole il divieto di sosta con eccezione per gli autobus di linea"],
    priority: 72,
    variant: 'alternate',
  }),
  asset({
    id: "Divieto di fermata limitato agli autoarticolati",
    topicId: 'stopping_parking',
    labels: ["Divieto di fermata autoarticolati","Fermata vietata autoarticolati"],
    aliases: ["divieto fermata autoarticolati","fermata vietata camion articolati","autoarticolati fermata"],
    useCases: ["Studente vuole il divieto di fermata limitato ai soli autoarticolati"],
    priority: 71,
    variant: 'alternate',
  }),
  asset({
    id: "Divieto di sosta e di fermata (con pannello direzione)",
    topicId: 'stopping_parking',
    labels: ["Divieto di sosta e fermata con direzione","Sosta e fermata vietate direzione"],
    aliases: ["divieto sosta e fermata direzione","freccia a destra sosta fermata","pannello direzione divieto"],
    useCases: ["Studente vuole il divieto di sosta e fermata col pannello freccia di direzione"],
    priority: 70,
    variant: 'alternate',
  }),
  asset({
    id: "Divieto di fermata con validità nei due sensi",
    topicId: 'stopping_parking',
    labels: ["Divieto di fermata nei due sensi","Fermata vietata bilaterale"],
    aliases: ["divieto fermata due sensi","fermata vietata entrambi i lati","frecce contrapposte fermata"],
    useCases: ["Studente vuole il divieto di fermata esteso a entrambi i lati del palo"],
    priority: 69,
    variant: 'alternate',
  }),
  asset({
    id: "Divieto di fermata con pannello integrativo di fine",
    topicId: 'stopping_parking',
    labels: ["Divieto di fermata tratto in avanti","Fermata vietata freccia in basso"],
    aliases: ["divieto fermata tratto avanti","freccia in basso fermata","fermata vietata tratto successivo"],
    useCases: ["Studente vuole il divieto di fermata col pannello freccia in basso per il tratto successivo"],
    priority: 68,
    variant: 'alternate',
  }),
  asset({
    id: "Divieto di sosta in entrambi i sensi (estensione bilaterale)",
    topicId: 'stopping_parking',
    labels: ["Divieto di sosta nei due sensi","Sosta vietata bilaterale"],
    aliases: ["divieto sosta due sensi","sosta vietata entrambi i lati","doppia freccia sosta"],
    useCases: ["Studente vuole il divieto di sosta esteso in entrambi i sensi col pannello doppia freccia"],
    priority: 67,
    variant: 'alternate',
  }),
  asset({
    id: "Divieto di sosta (con pannello indicante la direzione del divieto)",
    topicId: 'stopping_parking',
    labels: ["Divieto di sosta con direzione","Sosta vietata freccia in alto"],
    aliases: ["divieto sosta direzione","freccia in alto sosta","sosta vietata tratto successivo"],
    useCases: ["Studente vuole il divieto di sosta col pannello freccia in alto per il tratto successivo"],
    priority: 66,
    variant: 'alternate',
  }),
  asset({
    id: "Fine del divieto di sosta",
    topicId: 'stopping_parking',
    labels: ["Fine del divieto di sosta","Sosta di nuovo consentita"],
    aliases: ["fine divieto di sosta","freccia in basso fine sosta","sosta di nuovo consentita","termine divieto sosta"],
    useCases: ["Studente vuole il pannello freccia in basso che indica la fine del divieto di sosta"],
    priority: 65,
    variant: 'alternate',
  }),
  asset({
    id: "Continuazione del divieto di sosta",
    topicId: 'stopping_parking',
    labels: ["Continuazione del divieto di sosta","Sosta vietata continua"],
    aliases: ["continuazione divieto sosta","doppia freccia continuazione","divieto sosta continua","frecce su e giu sosta"],
    useCases: ["Studente vuole il pannello a doppia freccia che indica la continuazione del divieto di sosta"],
    priority: 64,
    variant: 'alternate',
  }),
  // ── safety_distance (2) ──
  asset({
    id: "Pannelli distanziometrici (tre, due, una banda)",
    topicId: 'safety_distance',
    labels: ["Pannelli distanziometrici","Pannelli a bande"],
    aliases: ["pannelli distanziometrici","pannelli a bande","tre due una banda","bande distanza","cartelli con le bande"],
    useCases: ["Leggere i pannelli a bande che contano la distanza residua dal punto pericoloso"],
    priority: 1000,
    variant: 'primary',
  }),
  asset({
    id: "Distanza minima fra veicoli",
    topicId: 'safety_distance',
    labels: ["Distanza minima fra veicoli","Distanza di sicurezza minima"],
    aliases: ["distanza minima fra veicoli","distanza minima tra veicoli","distanza di sicurezza","mantenere la distanza","distanza minima dal veicolo che precede"],
    useCases: ["Riconoscere il divieto che impone una distanza minima fissa dal veicolo che precede"],
    priority: 100,
    variant: 'primary',
  }),
  // ── mandatory (34) — P8 Step 2 ──
  asset({
    id: "Direzione obbligatoria a destra",
    topicId: 'mandatory',
    labels: ["Direzione obbligatoria a destra"],
    aliases: ["direzione obbligatoria a destra","obbligo svolta destra","freccia curva destra","devo girare a destra","cerchio blu freccia destra"],
    useCases: ["spiegare l'obbligo di svolta a destra all'intersezione","distinguere da passaggio obbligatorio a destra"],
    priority: 100,
    variant: 'primary',
  }),
  asset({
    id: "Direzione obbligatoria a sinistra",
    topicId: 'mandatory',
    labels: ["Direzione obbligatoria a sinistra"],
    aliases: ["direzione obbligatoria a sinistra","obbligo svolta sinistra","freccia curva sinistra","devo girare a sinistra","cerchio blu freccia sinistra"],
    useCases: ["spiegare l'obbligo di svolta a sinistra all'intersezione","distinguere da curva pericolosa a sinistra"],
    priority: 99,
    variant: 'primary',
  }),
  asset({
    id: "Direzione obbligatoria diritto",
    topicId: 'mandatory',
    labels: ["Direzione obbligatoria diritto"],
    aliases: ["direzione obbligatoria diritto","obbligo proseguire dritto","freccia su dritto","devo andare dritto","vietato svoltare"],
    useCases: ["spiegare l'obbligo di proseguire diritto","distinguere obbligo (cerchio) da indicazione (rettangolo)"],
    priority: 98,
    variant: 'primary',
  }),
  asset({
    id: "Catene da neve obbligatorie",
    topicId: 'mandatory',
    labels: ["Catene da neve obbligatorie"],
    aliases: ["catene da neve obbligatorie","obbligo catene","catene neve","pneumatici invernali obbligo","ruota con catena"],
    useCases: ["spiegare l'obbligo di catene o gomme invernali","distinguere obbligo equipaggiamento da avviso ghiaccio"],
    priority: 96,
    variant: 'primary',
  }),
  asset({
    id: "Rotatoria",
    topicId: 'mandatory',
    labels: ["Rotatoria"],
    aliases: ["rotatoria obbligo","senso rotatorio","frecce circolari blu","obbligo rotatoria","girare antiorario"],
    useCases: ["spiegare l'obbligo del senso rotatorio antiorario","distinguere rotatoria-obbligo (blu) da preavviso-pericolo (triangolo)"],
    priority: 95,
    variant: 'primary',
  }),
  asset({
    id: "Pista ciclabile",
    topicId: 'mandatory',
    labels: ["Pista ciclabile"],
    aliases: ["pista ciclabile obbligo","corsia biciclette","cerchio blu bicicletta","obbligo ciclisti","percorso bici"],
    useCases: ["spiegare l'obbligo per i ciclisti di usare la pista","distinguere obbligo (cerchio blu) da divieto bici"],
    priority: 94,
    variant: 'primary',
  }),
  asset({
    id: "Percorso pedonale",
    topicId: 'mandatory',
    labels: ["Percorso pedonale"],
    aliases: ["percorso pedonale obbligo","solo pedoni","cerchio blu pedone","riservato pedoni","obbligo a piedi"],
    useCases: ["spiegare l'obbligo del percorso riservato ai pedoni","distinguere da attraversamento pedonale (quadrato indicazione)"],
    priority: 93,
    variant: 'primary',
  }),
  asset({
    id: "Passaggio obbligatorio a destra",
    topicId: 'mandatory',
    labels: ["Passaggio obbligatorio a destra"],
    aliases: ["passaggio obbligatorio a destra","freccia obliqua destra","passa a destra ostacolo","aggirare ostacolo destra","spartitraffico destra"],
    useCases: ["spiegare l'obbligo di passare a destra di un ostacolo","distinguere passaggio (freccia obliqua) da direzione (freccia curva)"],
    priority: 92,
    variant: 'primary',
  }),
  asset({
    id: "Passaggio obbligatorio a sinistra",
    topicId: 'mandatory',
    labels: ["Passaggio obbligatorio a sinistra"],
    aliases: ["passaggio obbligatorio a sinistra","freccia obliqua sinistra","passa a sinistra ostacolo","aggirare ostacolo sinistra","spartitraffico sinistra"],
    useCases: ["spiegare l'obbligo di passare a sinistra di un ostacolo","distinguere passaggio (obliqua 45°) da direzione obbligatoria (orizzontale)"],
    priority: 91,
    variant: 'primary',
  }),
  asset({
    id: "Passaggi consentiti a destra o a sinistra (di un ostacolo)",
    topicId: 'mandatory',
    labels: ["Passaggi consentiti a destra o a sinistra (di un ostacolo)"],
    aliases: ["passaggi consentiti destra o sinistra","due frecce ostacolo","passare da entrambi i lati","scelta lato ostacolo","isola di traffico"],
    useCases: ["spiegare che si può passare da entrambi i lati dell'ostacolo","distinguere due-frecce (scelta) da una-freccia (obbligo singolo)"],
    priority: 90,
    variant: 'primary',
  }),
  asset({
    id: "Direzioni consentite (dritto e a sinistra)",
    topicId: 'mandatory',
    labels: ["Direzioni consentite (dritto e a sinistra)"],
    aliases: ["direzioni consentite dritto e sinistra","dritto o sinistra","due frecce dritto sinistra","obbligo scelta dritto sinistra"],
    useCases: ["spiegare le sole direzioni consentite dritto/sinistra","distinguere direzioni consentite (più frecce) da obbligatoria (singola)"],
    priority: 89,
    variant: 'primary',
  }),
  asset({
    id: "Direzioni consentite (dritto e destra)",
    topicId: 'mandatory',
    labels: ["Direzioni consentite (dritto e destra)"],
    aliases: ["direzioni consentite dritto e destra","dritto o destra","due frecce dritto destra","vietato svolta sinistra implicito"],
    useCases: ["spiegare le sole direzioni consentite dritto/destra","spiegare il divieto implicito di svolta a sinistra"],
    priority: 88,
    variant: 'primary',
  }),
  asset({
    id: "Direzioni consentite a destra e a sinistra",
    topicId: 'mandatory',
    labels: ["Direzioni consentite a destra e a sinistra"],
    aliases: ["direzioni consentite destra e sinistra","destra o sinistra obbligo","vietato dritto","due frecce laterali"],
    useCases: ["spiegare l'obbligo di svoltare a destra o a sinistra (no dritto)","distinguere da doppia curva pericolo"],
    priority: 87,
    variant: 'primary',
  }),
  asset({
    id: "Pista ciclopedonale promiscua",
    topicId: 'mandatory',
    labels: ["Pista ciclopedonale promiscua"],
    aliases: ["pista ciclopedonale promiscua","pedone e bici insieme","percorso promiscuo","simboli sovrapposti pedone bici","uso comune pedoni ciclisti"],
    useCases: ["spiegare la pista condivisa promiscua pedoni+bici","distinguere promiscua (sovrapposti) da separata (linea divisoria)"],
    priority: 70,
    variant: 'alternate',
  }),
  asset({
    id: "Pista pedonale e ciclabile separate (lato sinistro pedoni, lato destro biciclette)",
    topicId: 'mandatory',
    labels: ["Pista pedonale e ciclabile separate (lato sinistro pedoni, lato destro biciclette)"],
    aliases: ["pista pedonale e ciclabile separate","cerchio diviso pedone bici","linea verticale divisoria","pedoni a sinistra bici a destra"],
    useCases: ["spiegare la pista separata con corsie distinte","distinguere separata (linea divisoria) da promiscua (sovrapposti)"],
    priority: 69,
    variant: 'alternate',
  }),
  asset({
    id: "Percorso pedonale con pannello di inizio (freccia in su)",
    topicId: 'mandatory',
    labels: ["Percorso pedonale con pannello di inizio (freccia in su)"],
    aliases: ["percorso pedonale inizio","pannello freccia in su","inizio percorso pedonale","freccia verso l'alto pedonale"],
    useCases: ["spiegare l'inizio del percorso pedonale col pannello freccia","distinguere percorso pedonale da pista promiscua"],
    priority: 68,
    variant: 'alternate',
  }),
  asset({
    id: "Percorso pedonale (con limitazione festiva oraria)",
    topicId: 'mandatory',
    labels: ["Percorso pedonale (con limitazione festiva oraria)"],
    aliases: ["percorso pedonale limitazione festiva","pannello giorni festivi","croce festivi pedonale","limitazione oraria pedoni"],
    useCases: ["spiegare il percorso pedonale con validità nei festivi","spiegare la croce = giorni festivi, non cimitero"],
    priority: 67,
    variant: 'alternate',
  }),
  asset({
    id: "Pista ciclabile con limitazione oraria nei giorni festivi",
    topicId: 'mandatory',
    labels: ["Pista ciclabile con limitazione oraria nei giorni festivi"],
    aliases: ["pista ciclabile limitazione festiva","ciclabile giorni festivi","pannello festivo bici","limitazione oraria ciclabile"],
    useCases: ["spiegare la pista ciclabile con limitazione nei festivi","distinguere ciclabile-obbligo (cerchio) da indicazione (rettangolo)"],
    priority: 66,
    variant: 'alternate',
  }),
  asset({
    id: "Indicazione di velocità minima per corsia su autostrada",
    topicId: 'mandatory',
    labels: ["Indicazione di velocità minima per corsia su autostrada"],
    aliases: ["velocità minima per corsia autostrada","minima per corsia","dischi blu velocità","sfondo verde autostrada minima"],
    useCases: ["spiegare le velocità minime obbligatorie per corsia in autostrada","distinguere minima (dischi blu) da massima (cerchio rosso)"],
    priority: 65,
    variant: 'alternate',
  }),
  asset({
    id: "Direzione obbligatoria a destra (per autocarri)",
    topicId: 'mandatory',
    labels: ["Direzione obbligatoria a destra (per autocarri)"],
    aliases: ["direzione obbligatoria destra autocarri","obbligo destra camion","silhouette autocarro","freccia destra per camion"],
    useCases: ["spiegare l'obbligo di destra riservato agli autocarri","spiegare che vale solo per i veicoli raffigurati"],
    priority: 64,
    variant: 'alternate',
  }),
  asset({
    id: "Direzione obbligatoria — preselezione per autotreni e autoarticolati",
    topicId: 'mandatory',
    labels: ["Direzione obbligatoria — preselezione per autotreni e autoarticolati"],
    aliases: ["preselezione autotreni autoarticolati","obbligo dritto autotreni","fondo giallo temporaneo","preselezione mezzi pesanti"],
    useCases: ["spiegare la preselezione di direzione per autotreni/autoarticolati","spiegare il fondo giallo = segnaletica temporanea"],
    priority: 63,
    variant: 'alternate',
  }),
  asset({
    id: "Passaggio obbligatorio a destra (segnale temporaneo su base mobile)",
    topicId: 'mandatory',
    labels: ["Passaggio obbligatorio a destra (segnale temporaneo su base mobile)"],
    aliases: ["passaggio obbligatorio destra temporaneo","base mobile gialla","segnale cantiere passaggio destra","cono temporaneo destra"],
    useCases: ["spiegare il passaggio obbligatorio a destra temporaneo da cantiere","spiegare la base gialla = temporaneità"],
    priority: 62,
    variant: 'alternate',
  }),
  asset({
    id: "Senso unico parallelo, varianti sinistra/destra",
    topicId: 'information',
    labels: ["Senso unico parallelo (sinistra / destra)"],
    // The passaggio-obbligatorio vocabulary is dropped: that is a ROUND blue
    // sign on three other cards, and this rectangular panel is not it.
    aliases: ["senso unico parallelo","strada parallela a senso unico","freccia bianca su rettangolo blu"],
    useCases: ["spiegare il senso unico della strada parallela","distinguere il senso unico parallelo (rettangolo, indicazione) dal passaggio obbligatorio (cerchio, obbligo)"],
    priority: 61,
    variant: 'alternate',
  }),
  asset({
    id: "Percorso obbligato per cavalli (percorso ippico)",
    topicId: 'mandatory',
    labels: ["Percorso obbligato per cavalli (percorso ippico)"],
    aliases: ["percorso obbligato cavalli","percorso ippico","cavallo e cavaliere","riservato cavalli"],
    useCases: ["spiegare il percorso riservato a cavalli e cavalieri","distinguere obbligo (cerchio blu) da pericolo animali (triangolo)"],
    priority: 60,
    variant: 'alternate',
  }),
  asset({
    id: "Alt Polizia (posto di blocco)",
    topicId: 'mandatory',
    labels: ["Alt Polizia (posto di blocco)"],
    aliases: ["alt polizia","posto di blocco","fermarsi polizia","controllo polizia obbligo arresto"],
    useCases: ["spiegare l'obbligo di arresto al posto di blocco di polizia","distinguere alt polizia (temporaneo) da stop (precedenza)"],
    priority: 59,
    variant: 'alternate',
  }),
  asset({
    id: "Alt stazione (controllo o pedaggio)",
    topicId: 'mandatory',
    labels: ["Alt stazione (controllo o pedaggio)"],
    aliases: ["alt stazione","casello pedaggio","fermarsi stazione","posto di controllo arresto"],
    useCases: ["spiegare l'obbligo di arresto al casello/posto di controllo","distinguere alt stazione da alt polizia e alt dogana"],
    priority: 58,
    variant: 'alternate',
  }),
  asset({
    id: "Confine doganale (Alt dogana)",
    topicId: 'mandatory',
    labels: ["Confine doganale (Alt dogana)"],
    aliases: ["alt dogana","confine doganale","controllo doganale arresto","fermarsi dogana"],
    useCases: ["spiegare l'obbligo di arresto al controllo doganale di confine","distinguere alt dogana (cerchio) da stop (ottagono)"],
    priority: 57,
    variant: 'alternate',
  }),
  asset({
    id: "Segnalazione del vigile — braccia aperte (di profilo)",
    topicId: 'mandatory',
    labels: ["Segnalazione del vigile — braccia aperte (di profilo)"],
    aliases: ["vigile braccia aperte","vigile di profilo","segnale manuale vigile via libera","agente braccia orizzontali"],
    useCases: ["spiegare il vigile di profilo a braccia aperte = via libera laterale","spiegare che frontalmente/dietro equivale a stop"],
    priority: 56,
    variant: 'alternate',
  }),
  asset({
    id: "Fine pista ciclabile",
    topicId: 'mandatory',
    labels: ["Fine pista ciclabile"],
    aliases: ["fine pista ciclabile","termine ciclabile","barra rossa bicicletta","cessa obbligo ciclabile"],
    useCases: ["spiegare la fine dell'obbligo di pista ciclabile","spiegare barra rossa su blu = fine obbligo, non divieto"],
    priority: 55,
    variant: 'alternate',
  }),
  asset({
    id: "Fine del percorso pedonale",
    topicId: 'mandatory',
    labels: ["Fine del percorso pedonale"],
    aliases: ["fine percorso pedonale","termine percorso pedoni","barra rossa pedone","cessa obbligo pedonale"],
    useCases: ["spiegare la fine dell'obbligo del percorso pedonale","distinguere fine (barra rossa) da inizio percorso pedonale"],
    priority: 54,
    variant: 'alternate',
  }),
  asset({
    id: "Fine del percorso pedonale e ciclabile",
    topicId: 'mandatory',
    labels: ["Fine del percorso pedonale e ciclabile"],
    aliases: ["fine percorso pedonale e ciclabile","termine ciclopedonale","barra rossa pedone bici","cessa obbligo promiscuo"],
    useCases: ["spiegare la fine dell'obbligo del percorso pedonale+ciclabile","spiegare sfondo blu + barra = fine obbligo"],
    priority: 53,
    variant: 'alternate',
  }),
  asset({
    id: "Fine percorso pedonale e ciclabile separato",
    topicId: 'mandatory',
    labels: ["Fine percorso pedonale e ciclabile separato"],
    aliases: ["fine percorso pedonale e ciclabile separato","termine pista separata","barra rossa pista separata","cessa obbligo separato"],
    useCases: ["spiegare la fine del percorso separato pedoni/bici","distinguere fine (barra) da inizio percorso separato"],
    priority: 52,
    variant: 'alternate',
  }),
  asset({
    id: "Fine del limite minimo di velocità",
    topicId: 'mandatory',
    labels: ["Fine del limite minimo di velocità"],
    aliases: ["fine limite minimo velocità","termine velocità minima","barra rossa su 30 blu","cessa obbligo minima"],
    useCases: ["spiegare la fine dell'obbligo di velocità minima","distinguere fine minima (blu) da fine massima (bianco)"],
    priority: 51,
    variant: 'alternate',
  }),
  asset({
    id: "Fine del percorso obbligato per cavalli",
    topicId: 'mandatory',
    labels: ["Fine del percorso obbligato per cavalli"],
    aliases: ["fine percorso obbligato cavalli","termine percorso ippico","barra rossa cavallo","cessa obbligo cavalli"],
    useCases: ["spiegare la fine del percorso riservato ai cavalli","distinguere fine (barra) da inizio percorso ippico"],
    priority: 50,
    variant: 'alternate',
  }),
  // ── prohibitions (34) — P8 Step 2 ──
  asset({
    id: "Senso vietato",
    topicId: 'prohibitions',
    labels: ["Senso vietato","Divieto di accesso"],
    aliases: ["senso vietato","divieto di accesso","fascia bianca orizzontale","disco rosso barra bianca","contromano"],
    useCases: ["riconoscere il senso vietato","distinguere senso vietato da divieto di transito"],
    priority: 100,
    variant: 'primary',
  }),
  asset({
    id: "Transito vietato in entrambi i sensi (divieto di transito)",
    topicId: 'prohibitions',
    labels: ["Divieto di transito","Transito vietato in entrambi i sensi"],
    aliases: ["divieto di transito","cerchio rosso vuoto","transito vietato entrambi i sensi","divieto a tutti i veicoli"],
    useCases: ["riconoscere il divieto di transito generale","distinguere dal senso vietato"],
    priority: 99,
    variant: 'primary',
  }),
  asset({
    id: "Fine di tutti i divieti precedentemente segnalati",
    topicId: 'prohibitions',
    labels: ["Fine di tutti i divieti","Fine divieti"],
    aliases: ["fine di tutti i divieti","fine divieti","banda obliqua","cerchio bianco senza simboli"],
    useCases: ["riconoscere la fine di tutti i divieti","distinguere dai fine divieto specifici"],
    priority: 98,
    variant: 'primary',
  }),
  asset({
    id: "Larghezza massima",
    topicId: 'prohibitions',
    labels: ["Larghezza massima"],
    aliases: ["larghezza massima","triangoli neri orizzontali","limite di larghezza","2,30 m"],
    useCases: ["riconoscere il limite di larghezza","distinguere larghezza da altezza e lunghezza"],
    priority: 97,
    variant: 'primary',
  }),
  asset({
    id: "Altezza massima",
    topicId: 'prohibitions',
    labels: ["Altezza massima"],
    aliases: ["altezza massima","triangoli verticali","limite di altezza","3,50 m"],
    useCases: ["riconoscere il limite di altezza","distinguere altezza da larghezza"],
    priority: 96,
    variant: 'primary',
  }),
  asset({
    id: "Divieto di transito ai pedoni",
    topicId: 'prohibitions',
    labels: ["Divieto di transito ai pedoni","Transito vietato ai pedoni"],
    aliases: ["divieto di transito ai pedoni","vietato ai pedoni","sagoma pedone cerchio rosso","divieto pedoni"],
    useCases: ["riconoscere il divieto ai pedoni","distinguere dal percorso pedonale obbligatorio e dal pericolo attraversamento"],
    priority: 95,
    variant: 'primary',
  }),
  asset({
    id: "Divieto di segnalazioni acustiche",
    topicId: 'prohibitions',
    labels: ["Divieto di segnalazioni acustiche"],
    aliases: ["divieto di segnalazioni acustiche","vietato clacson","tromba barrata","divieto clacson"],
    useCases: ["riconoscere il divieto di clacson","distinguere dall'obbligo di segnalazioni acustiche"],
    priority: 94,
    variant: 'primary',
  }),
  asset({
    id: "Transito vietato ai veicoli aventi massa superiore a 7 tonnellate",
    topicId: 'prohibitions',
    labels: ["Massa massima 7 t","Transito vietato ai veicoli oltre 7 tonnellate"],
    aliases: ["massa superiore a 7 tonnellate","limite di massa","7 t","divieto per peso"],
    useCases: ["riconoscere il limite di massa complessiva","distinguere massa effettiva da massa per asse"],
    priority: 93,
    variant: 'primary',
  }),
  asset({
    id: "Transito vietato agli autoveicoli",
    topicId: 'prohibitions',
    labels: ["Transito vietato agli autoveicoli"],
    aliases: ["transito vietato agli autoveicoli","vietato alle auto","sagoma frontale auto","divieto autoveicoli"],
    useCases: ["riconoscere il divieto agli autoveicoli","distinguere dal divieto generale di transito"],
    priority: 92,
    variant: 'primary',
  }),
  asset({
    id: "Transito vietato ai veicoli di trasporto cose",
    topicId: 'prohibitions',
    labels: ["Transito vietato ai veicoli di trasporto cose","Divieto autocarri"],
    aliases: ["transito vietato trasporto cose","vietato autocarri","sagoma autocarro","divieto camion merci"],
    useCases: ["riconoscere il divieto ai mezzi merci","distinguere dagli autobus e dal divieto generale"],
    priority: 91,
    variant: 'primary',
  }),
  asset({
    id: "Transito vietato ai motocicli",
    topicId: 'prohibitions',
    labels: ["Transito vietato ai motocicli"],
    aliases: ["transito vietato ai motocicli","vietato moto","sagoma motociclista casco","divieto motocicli"],
    useCases: ["riconoscere il divieto ai motocicli","distinguere motociclo da ciclomotore"],
    priority: 90,
    variant: 'primary',
  }),
  asset({
    id: "Transito vietato alle biciclette",
    topicId: 'prohibitions',
    labels: ["Transito vietato alle biciclette"],
    aliases: ["transito vietato alle biciclette","vietato bici","sagoma bicicletta cerchio rosso","divieto velocipedi"],
    useCases: ["riconoscere il divieto alle biciclette","distinguere dall'obbligo di pista ciclabile"],
    priority: 89,
    variant: 'primary',
  }),
  asset({
    id: "Divieto di transito agli autobus",
    topicId: 'prohibitions',
    labels: ["Divieto di transito agli autobus"],
    aliases: ["divieto di transito agli autobus","vietato autobus","sagoma autobus cerchio rosso","divieto pullman"],
    useCases: ["riconoscere il divieto agli autobus","distinguere dalla fermata autobus blu"],
    priority: 70,
    variant: 'alternate',
  }),
  asset({
    id: "Transito vietato ai veicoli che trasportano merci pericolose",
    topicId: 'prohibitions',
    labels: ["Transito vietato ai veicoli con merci pericolose"],
    aliases: ["merci pericolose","pannello arancione adr","cisterna merci pericolose","divieto merci pericolose"],
    useCases: ["riconoscere il divieto per merci pericolose","distinguere dal divieto esplosivi"],
    priority: 69,
    variant: 'alternate',
  }),
  asset({
    id: "Transito vietato ai veicoli che trasportano esplosivi",
    topicId: 'prohibitions',
    labels: ["Transito vietato ai veicoli con esplosivi"],
    aliases: ["trasportano esplosivi","deflagrazione fiamma","vietato infiammabili","divieto esplosivi"],
    useCases: ["riconoscere il divieto per esplosivi e infiammabili","distinguere dalle merci pericolose ADR"],
    priority: 68,
    variant: 'alternate',
  }),
  asset({
    id: "Transito vietato ai veicoli con massa per asse superiore a 2,5 t",
    topicId: 'prohibitions',
    labels: ["Massa per asse massima 2,5 t"],
    aliases: ["massa per asse","asse con ruote freccia","2,5 t per asse","limite asse"],
    useCases: ["riconoscere il limite di massa per asse","distinguere massa per asse da massa totale"],
    priority: 67,
    variant: 'alternate',
  }),
  asset({
    id: "Transito vietato ai veicoli di lunghezza superiore a 10 m",
    topicId: 'prohibitions',
    labels: ["Lunghezza massima 10 m"],
    aliases: ["lunghezza superiore a 10 m","frecce orizzontali lunghezza","10 m","limite di lunghezza"],
    useCases: ["riconoscere il limite di lunghezza","distinguere lunghezza da massa e altezza"],
    priority: 66,
    variant: 'alternate',
  }),
  asset({
    id: "Transito vietato agli autoveicoli trainanti un rimorchio",
    topicId: 'prohibitions',
    labels: ["Transito vietato agli autoveicoli con rimorchio"],
    aliases: ["trainanti un rimorchio","motrice con rimorchio","vietato traino rimorchio","divieto rimorchio"],
    useCases: ["riconoscere il divieto per veicoli con rimorchio","distinguere dal divieto autocarri singoli"],
    priority: 64,
    variant: 'alternate',
  }),
  asset({
    id: "Transito vietato ai veicoli a trazione animale",
    topicId: 'prohibitions',
    labels: ["Transito vietato ai veicoli a trazione animale"],
    aliases: ["trazione animale","cavallo che traina un carro","vietato carri animali","divieto trazione animale"],
    useCases: ["riconoscere il divieto ai veicoli a trazione animale","distinguere dal pericolo animali vaganti"],
    priority: 63,
    variant: 'alternate',
  }),
  asset({
    id: "Transito vietato ai carretti a mano",
    topicId: 'prohibitions',
    labels: ["Transito vietato ai carretti a mano"],
    aliases: ["carretti a mano","persona che spinge carretto","vietato carretti","divieto carretto a spinta"],
    useCases: ["riconoscere il divieto ai carretti a mano","distinguere dal divieto pedoni"],
    priority: 62,
    variant: 'alternate',
  }),
  asset({
    id: "Transito vietato ai veicoli di trasporto cose con massa superiore a 6,5 t",
    topicId: 'prohibitions',
    labels: ["Transito vietato trasporto cose oltre 6,5 t"],
    aliases: ["trasporto cose oltre 6,5 t","autocarro con 6,5t","vietato camion pesanti","limite massa merci"],
    useCases: ["riconoscere il divieto merci sopra una massa specifica","distinguere dal divieto trasporto cose generico"],
    priority: 61,
    variant: 'alternate',
  }),
  asset({
    id: "Transito vietato ad autotreni e autoarticolati",
    topicId: 'prohibitions',
    labels: ["Transito vietato a tutti i veicoli (con pittogrammi mezzi pesanti)"],
    aliases: ["transito vietato con pittogrammi","cerchio rosso con mezzi pesanti sotto","divieto a tutti i veicoli pittogrammi","pannello mezzi pesanti"],
    useCases: ["riconoscere il divieto generale con pittogrammi esemplificativi","distinguere dal senso vietato"],
    priority: 60,
    variant: 'alternate',
  }),
  asset({
    id: "Senso vietato con eccezione per autobus",
    topicId: 'prohibitions',
    labels: ["Senso vietato con eccezione per autobus"],
    aliases: ["senso vietato eccetto autobus","disco rosso barra bianca pannello bus","eccetto autobus","deroga autobus"],
    useCases: ["riconoscere il senso vietato con deroga per gli autobus di linea","distinguere dal divieto agli autobus"],
    priority: 59,
    variant: 'alternate',
  }),
  asset({
    id: "Senso vietato — eccetto autobus",
    topicId: 'prohibitions',
    labels: ["Senso vietato — eccetto autobus"],
    aliases: ["senso vietato eccetto autobus frontale","pannello eccetto bus","deroga bus di linea","senso vietato deroga bus"],
    useCases: ["riconoscere il senso vietato con eccezione bus (icona frontale)","distinguere dai pullman turistici esclusi"],
    priority: 58,
    variant: 'alternate',
  }),
  asset({
    id: "Divieto di transito ai motocicli con limitazione oraria (7:30-19:00)",
    topicId: 'prohibitions',
    labels: ["Divieto motocicli con orario 7:30-19:00"],
    aliases: ["divieto motocicli orario","motociclista con pannello orario","7,30-19,00 moto","divieto moto fascia oraria"],
    useCases: ["riconoscere il divieto motocicli limitato a una fascia oraria","capire che l'orario restringe il divieto"],
    priority: 57,
    variant: 'alternate',
  }),
  asset({
    id: "Transito vietato ai motocicli — nei giorni festivi 8,00-20,00",
    topicId: 'prohibitions',
    labels: ["Divieto motocicli giorni festivi 8-20"],
    aliases: ["motocicli giorni festivi","croce festivi orario moto","8,00-20,00 festivi","divieto moto festivi"],
    useCases: ["riconoscere il divieto motocicli nei giorni festivi","distinguere croce festivi da martelli lavorativi"],
    priority: 56,
    variant: 'alternate',
  }),
  asset({
    id: "Divieto di transito alle macchine agricole (trattori)",
    topicId: 'prohibitions',
    labels: ["Divieto macchine agricole","Divieto trattori"],
    aliases: ["macchine agricole","trattore ruote asimmetriche","vietato trattrici","divieto trattori"],
    useCases: ["riconoscere il divieto alle macchine agricole","distinguere dal divieto trazione animale"],
    priority: 55,
    variant: 'alternate',
  }),
  asset({
    id: "Divieto di transito specifico per autoarticolati / autotreni",
    topicId: 'prohibitions',
    labels: ["Divieto autoarticolati / autotreni"],
    aliases: ["autoarticolati autotreni","pannello con autoarticolato","vietato tir","divieto motrice semirimorchio"],
    useCases: ["riconoscere il divieto specifico per autoarticolati","capire il ruolo del pannello che specifica la categoria"],
    priority: 54,
    variant: 'alternate',
  }),
  asset({
    id: "Divieto di transito agli autocarri (con limitazione oraria nei giorni lavorativi)",
    topicId: 'prohibitions',
    labels: ["Divieto autocarri giorni lavorativi"],
    aliases: ["autocarri giorni lavorativi","martelli incrociati orario","8,00-20,00 autocarri","divieto camion feriali"],
    useCases: ["riconoscere il divieto autocarri nei giorni lavorativi","non confondere i martelli con lavori in corso"],
    priority: 53,
    variant: 'alternate',
  }),
  asset({
    id: "Divieto di transito (limitato ai giorni lavorativi nelle ore indicate)",
    topicId: 'prohibitions',
    labels: ["Divieto di transito giorni lavorativi"],
    aliases: ["divieto transito giorni lavorativi","martelli incrociati feriali","cerchio rosso vuoto orario feriale","divieto feriale orario"],
    useCases: ["riconoscere il divieto generale nei giorni lavorativi","distinguere martelli (feriali) da croce (festivi)"],
    priority: 52,
    variant: 'alternate',
  }),
  asset({
    id: "Divieto di transito (limitato ai giorni festivi nelle ore indicate)",
    topicId: 'prohibitions',
    labels: ["Divieto di transito giorni festivi"],
    aliases: ["divieto transito giorni festivi","croce nera festivi","cerchio rosso vuoto orario festivo","divieto festivo orario"],
    useCases: ["riconoscere il divieto generale nei giorni festivi","distinguere croce (festivi) da martelli (feriali)"],
    priority: 51,
    variant: 'alternate',
  }),
  asset({
    id: "Divieto di transito (con limitazione oraria)",
    topicId: 'prohibitions',
    labels: ["Divieto di transito con limitazione oraria"],
    aliases: ["divieto transito con orario","cerchio rosso vuoto pannello orario","7,30-19,00 transito","divieto fascia oraria"],
    useCases: ["riconoscere il divieto di transito limitato a una fascia oraria","distinguere dal senso vietato e dal fine divieti"],
    priority: 50,
    variant: 'alternate',
  }),
  asset({
    id: "Divieto di segnalazioni acustiche (con limitazione oraria)",
    topicId: 'prohibitions',
    labels: ["Divieto di segnalazioni acustiche con orario"],
    aliases: ["divieto clacson con orario","tromba barrata pannello orario","7,30-19,00 clacson","vietato clacson fascia oraria"],
    useCases: ["riconoscere il divieto di clacson limitato a una fascia oraria","leggere correttamente quando il divieto è attivo"],
    priority: 49,
    variant: 'alternate',
  }),
  asset({
    id: "Transito vietato ai pedoni (limitato alla fascia oraria indicata)",
    topicId: 'prohibitions',
    labels: ["Divieto pedoni con fascia oraria"],
    aliases: ["pedoni fascia oraria","pannello orario sopra pedone","7,30-19,00 pedoni","divieto pedoni orario"],
    useCases: ["riconoscere il divieto pedoni limitato a una fascia oraria","leggere correttamente la composizione A/B pannello-segnale"],
    priority: 48,
    variant: 'alternate',
  }),
  // ── information (75) — P8 Step 3 ──
  asset({
    id: "Parcheggio",
    topicId: 'information',
    labels: ["Parcheggio","Segnale di parcheggio (P)"],
    aliases: ["parcheggio","p blu","sosta","area di sosta","lettera p"],
    useCases: ["riconoscere il parcheggio generico","distinguere P da area di servizio"],
    priority: 100,
    variant: 'primary',
  }),
  asset({
    // 2026-07-04 curation fix: the owned asset (img_sign/194.png, manual fig.
    // 194) is the BLUE "Inizio strada extraurbana principale" sign — it was
    // mis-named "Inizio autostrada" at ingestion (the green fig. 1042 variant
    // is the autostrada sign and is not an owned asset).
    id: "Inizio strada extraurbana principale",
    topicId: 'information',
    labels: ["Inizio strada extraurbana principale","Segnale di inizio strada extraurbana principale"],
    aliases: ["inizio strada extraurbana principale","extraurbana principale","strada riservata ai veicoli a motore","cavalcavia blu","carreggiate separate blu"],
    useCases: ["riconoscere dove iniziano le regole della strada extraurbana principale (limite 110 km/h)","distinguere extraurbana principale (blu) da autostrada (verde, 130 km/h)"],
    priority: 99,
    variant: 'primary',
  }),
  asset({
    id: "Ospedale (con freccia di direzione)",
    topicId: 'information',
    labels: ["Ospedale","Ospedale con freccia"],
    aliases: ["ospedale","letto rosso","croce e letto","direzione ospedale"],
    useCases: ["trovare l'ospedale","distinguere ospedale da pronto soccorso"],
    priority: 98,
    variant: 'primary',
  }),
  asset({
    id: "Attraversamento pedonale",
    topicId: 'information',
    labels: ["Attraversamento pedonale (indicazione)","Strisce pedonali"],
    aliases: ["attraversamento pedonale","strisce pedonali","zebre","pedone fondo blu"],
    useCases: ["riconoscere la presenza di un attraversamento","distinguere dal triangolo di pericolo pedoni"],
    priority: 97,
    variant: 'primary',
  }),
  asset({
    id: "Pronto soccorso — direzione",
    topicId: 'information',
    labels: ["Pronto soccorso","Pronto soccorso direzione"],
    aliases: ["pronto soccorso","croce rossa","primo soccorso","presidio sanitario"],
    useCases: ["trovare il pronto soccorso","distinguere croce rossa da farmacia verde"],
    priority: 96,
    variant: 'primary',
  }),
  asset({
    id: "Posto di rifornimento (carburante)",
    topicId: 'information',
    labels: ["Posto di rifornimento","Distributore carburante"],
    aliases: ["distributore","carburante","pompa benzina","rifornimento","benzina"],
    useCases: ["trovare il distributore di carburante","distinguere dalla area di servizio completa"],
    priority: 95,
    variant: 'primary',
  }),
  asset({
    id: "Senso unico parallelo (frontale)",
    topicId: 'information',
    labels: ["Senso unico","Senso unico frontale"],
    aliases: ["senso unico","freccia bianca su blu","strada a senso unico"],
    useCases: ["riconoscere una strada a senso unico","distinguere dal passaggio obbligatorio"],
    priority: 94,
    variant: 'primary',
  }),
  asset({
    id: "Galleria",
    topicId: 'information',
    labels: ["Galleria","Segnale di galleria"],
    aliases: ["galleria","tunnel","imbocco galleria","fari in galleria"],
    useCases: ["riconoscere l'ingresso in galleria","distinguere dal sottopassaggio"],
    priority: 93,
    variant: 'primary',
  }),
  asset({
    id: "Segnale di preavviso di direzione (autostradale)",
    topicId: 'information',
    labels: ["Preavviso di direzione autostradale"],
    aliases: ["preavviso direzione autostrada","scudetto a1","preselezione verde","direzione bologna"],
    useCases: ["leggere la preselezione in autostrada","distinguere preavviso da conferma percorso"],
    priority: 80,
    variant: 'alternate',
  }),
  asset({
    id: "Segnale di preavviso di direzione su strada extraurbana",
    topicId: 'information',
    labels: ["Preavviso di direzione extraurbana"],
    aliases: ["preavviso direzione extraurbana","sfondo blu preavviso","preparati al cambio corsia"],
    useCases: ["prepararsi a un'intersezione extraurbana","distinguere preavviso da segnale di direzione all'incrocio"],
    priority: 79,
    variant: 'alternate',
  }),
  asset({
    id: "Preavviso di direzione (intersezione con itinerario turistico e passaggio a livello)",
    topicId: 'information',
    labels: ["Preavviso direzione con passaggio a livello"],
    aliases: ["preavviso direzione","itinerario turistico","passaggio a livello su freccia"],
    useCases: ["leggere le diramazioni all'intersezione","distinguere sfondo blu da verde"],
    priority: 78,
    variant: 'alternate',
  }),
  asset({
    id: "Preavviso di direzione complesso (con divieto agli autocarri su una direttrice)",
    topicId: 'information',
    labels: ["Preavviso direzione complesso con divieto autocarri"],
    aliases: ["preavviso complesso","divieto autocarri su freccia","pescara avezzano"],
    useCases: ["leggere segnali direzionali complessi","notare il divieto sulla freccia"],
    priority: 77,
    variant: 'alternate',
  }),
  asset({
    id: "Preavviso di direzione complesso (extraurbano + svolta urbana per centro e stazione)",
    topicId: 'information',
    labels: ["Preavviso direzione con svolta urbana"],
    aliases: ["preavviso complesso","sora napoli","svolta urbana centro stazione"],
    useCases: ["leggere svolte urbane e direttrici extraurbane","associare colore riquadro al tipo di strada"],
    priority: 75,
    variant: 'alternate',
  }),
  asset({
    id: "Preavviso di intersezione complessa con preselezione delle corsie e indicazione delle destinazioni",
    topicId: 'information',
    labels: ["Preavviso intersezione con preselezione corsie"],
    aliases: ["preselezione corsie","preavviso intersezione","senso vietato corsia centrale"],
    useCases: ["scegliere la corsia prima dell'incrocio","interpretare il senso vietato di corsia"],
    priority: 74,
    variant: 'alternate',
  }),
  asset({
    id: "Pannello direzionale (Milano dritto, centro a destra)",
    topicId: 'information',
    labels: ["Pannello direzionale urbano"],
    aliases: ["pannello direzionale","milano dritto","centro a destra","indicazione urbana"],
    useCases: ["seguire le direzioni all'incrocio in città","distinguere indicazione da obbligo di direzione"],
    priority: 73,
    variant: 'alternate',
  }),
  asset({
    id: "Preavviso di uscita autostradale — Frosinone (a 500 m)",
    topicId: 'information',
    labels: ["Preavviso uscita autostradale Frosinone"],
    aliases: ["preavviso uscita","frosinone 500 m","uscita autostrada","localita marrone"],
    useCases: ["anticipare l'uscita autostradale","interpretare le località su fondo marrone"],
    priority: 72,
    variant: 'alternate',
  }),
  asset({
    id: "Progressiva chilometrica autostradale (km 54) con uscita Piacenza a 4 km",
    topicId: 'information',
    labels: ["Preavviso uscita autostradale Piacenza"],
    aliases: ["preavviso uscita","piacenza uscita 54","uscita numerata","4 km distanza"],
    useCases: ["distinguere numero uscita da distanza in km","anticipare l'uscita numerata"],
    priority: 71,
    variant: 'alternate',
  }),
  asset({
    id: "Cavalcavia per inversione di marcia su autostrada / strada a carreggiate separate",
    topicId: 'information',
    labels: ["Cavalcavia inversione di marcia"],
    aliases: ["cavalcavia inversione","inversione di marcia autostrada","carreggiate separate"],
    useCases: ["sapere dove invertire la marcia","evitare di credere consentita ovunque l'inversione"],
    priority: 70,
    variant: 'alternate',
  }),
  asset({
    id: "Ponte",
    topicId: 'information',
    labels: ["Ponte"],
    // "extraurbana secondaria" dropped: the green ground makes this autostrada.
    aliases: ["ponte","cavalcavia","ponte ad arco"],
    useCases: ["riconoscere il segnale di ponte","distinguere ponte da galleria","leggere il colore di fondo: verde = autostrada"],
    priority: 69,
    variant: 'alternate',
  }),
  asset({
    id: "Aumento del numero di corsie",
    topicId: 'information',
    labels: ["Aumento del numero di corsie"],
    aliases: ["aumento corsie","corsia in piu","da una a due corsie"],
    useCases: ["anticipare nuove corsie disponibili","distinguere dal pericolo cambiamento corsie"],
    priority: 68,
    variant: 'alternate',
  }),
  asset({
    id: "Riduzione del numero delle corsie (in autostrada)",
    topicId: 'information',
    labels: ["Riduzione del numero delle corsie"],
    aliases: ["riduzione corsie","corsia che termina","immissione corsia","restringimento autostrada"],
    useCases: ["cedere il passo quando la corsia finisce","anticipare la riduzione di corsie"],
    priority: 67,
    variant: 'alternate',
  }),
  asset({
    id: "Strada riservata ai veicoli a motore",
    topicId: 'information',
    labels: ["Strada riservata ai veicoli a motore"],
    aliases: ["strada riservata veicoli a motore","no biciclette pedoni","sfondo blu auto"],
    useCases: ["sapere chi può circolare","distinguere da autostrada verde"],
    priority: 66,
    variant: 'alternate',
  }),
  asset({
    id: "Confine di Stato",
    topicId: 'information',
    labels: ["Confine di Stato (Francia)"],
    aliases: ["confine di stato","frontiera francia","stelle europee","valico"],
    useCases: ["riconoscere il confine nazionale","distinguere da indicazione di località"],
    priority: 65,
    variant: 'alternate',
  }),
  asset({
    id: "Segnale di confine di Stato (Italia, area UE)",
    topicId: 'information',
    labels: ["Confine di Stato (Italia, UE)"],
    aliases: ["confine italia","ingresso italia","corona di stelle","area ue"],
    useCases: ["riconoscere l'ingresso in Italia","distinguere dal segnale di inizio centro abitato"],
    priority: 64,
    variant: 'alternate',
  }),
  asset({
    id: "Strada senza sbocco",
    topicId: 'information',
    labels: ["Strada senza sbocco"],
    aliases: ["strada senza sbocco","fondo cieco","vicolo cieco","cul de sac frontale"],
    useCases: ["sapere che la strada termina","capire che non è un divieto"],
    priority: 63,
    variant: 'alternate',
  }),
  asset({
    id: "Strada senza uscita laterale a destra",
    topicId: 'information',
    labels: ["Strada senza uscita laterale a destra"],
    aliases: ["senza uscita laterale","braccio rosso a destra","derivazione chiusa"],
    useCases: ["capire quale braccio è chiuso","distinguere da senza uscita frontale"],
    priority: 62,
    variant: 'alternate',
  }),
  asset({
    id: "Parcheggio (con freccia di direzione)",
    topicId: 'information',
    labels: ["Parcheggio con freccia di direzione"],
    aliases: ["parcheggio con freccia","direzione parcheggio","p con freccia"],
    useCases: ["seguire la direzione del parcheggio","distinguere dalla fine area di parcheggio"],
    priority: 61,
    variant: 'alternate',
  }),
  asset({
    id: "Parcheggio — direzione e distanza",
    topicId: 'information',
    labels: ["Parcheggio direzione e distanza"],
    aliases: ["parcheggio a destra","parcheggio 300 m","distanza parcheggio"],
    useCases: ["raggiungere un parcheggio a distanza","non leggere i metri come limite di velocità"],
    priority: 60,
    variant: 'alternate',
  }),
  asset({
    id: "Parcheggio — direzione dritto",
    topicId: 'information',
    labels: ["Parcheggio direzione dritto"],
    aliases: ["parcheggio dritto","freccia in su parcheggio","prosegui per parcheggio"],
    useCases: ["raggiungere il parcheggio proseguendo dritto","distinguere freccia da obbligo di direzione"],
    priority: 59,
    variant: 'alternate',
  }),
  asset({
    id: "Parcheggio (situato a sinistra)",
    topicId: 'information',
    labels: ["Parcheggio situato a sinistra"],
    aliases: ["parcheggio a sinistra","freccia sinistra parcheggio"],
    useCases: ["individuare il parcheggio a sinistra","non leggere la freccia come direzione obbligata"],
    priority: 58,
    variant: 'alternate',
  }),
  asset({
    id: "Parcheggio (su entrambi i lati o in entrambe le direzioni)",
    topicId: 'information',
    labels: ["Parcheggio su entrambi i lati"],
    aliases: ["parcheggio entrambi i lati","frecce contrapposte","parcheggio doppio senso"],
    useCases: ["capire l'estensione del parcheggio","distinguere frecce divergenti da convergenti"],
    priority: 57,
    variant: 'alternate',
  }),
  asset({
    id: "Parcheggio (consentito nei giorni festivi nelle ore indicate)",
    topicId: 'information',
    labels: ["Parcheggio nei giorni festivi"],
    aliases: ["parcheggio festivi","croce giorni festivi","ore indicate"],
    useCases: ["sapere quando è consentito il parcheggio","distinguere croce festivi da martelli lavorativi"],
    priority: 56,
    variant: 'alternate',
  }),
  asset({
    id: "Parcheggio nei giorni lavorativi (8,00 - 20,00)",
    topicId: 'information',
    labels: ["Parcheggio nei giorni lavorativi"],
    aliases: ["parcheggio lavorativi","due martelli","fascia oraria 8 20"],
    useCases: ["sapere gli orari del parcheggio","non scambiare i martelli per lavori in corso"],
    priority: 55,
    variant: 'alternate',
  }),
  asset({
    id: "Parcheggio con esclusione degli autobus",
    topicId: 'information',
    labels: ["Parcheggio con esclusione autobus"],
    aliases: ["parcheggio eccetto autobus","esclusione autobus","eccetto bus"],
    useCases: ["capire chi è escluso dal parcheggio","interpretare correttamente 'eccetto'"],
    priority: 54,
    variant: 'alternate',
  }),
  asset({
    id: "Parcheggio con fermata di autobus e linee servite",
    topicId: 'information',
    labels: ["Parcheggio con fermata autobus"],
    aliases: ["parcheggio con fermata bus","linee servite","p e fermata autobus"],
    useCases: ["riconoscere parcheggio con interscambio bus","distinguere dal parcheggio riservato autobus"],
    priority: 53,
    variant: 'alternate',
  }),
  asset({
    id: "Velocità consigliata — 60 km/h",
    topicId: 'information',
    labels: ["Velocità consigliata 60"],
    aliases: ["velocita consigliata","60 quadrato blu","consigliata 60 km/h"],
    useCases: ["distinguere consiglio da obbligo","non confondere con limite massimo o minimo"],
    priority: 52,
    variant: 'alternate',
  }),
  asset({
    id: "Lanterna semaforica di “onda verde”",
    topicId: 'traffic_lights',
    labels: ["Lanterna semaforica di onda verde"],
    // NOT a limit: black discs with white digits are advisory speeds. The
    // "preavviso limite" vocabulary is dropped so it cannot read as a divieto.
    aliases: ["onda verde","velocità consigliata per i semafori","dischi neri con velocità","sincronia semafori"],
    useCases: ["capire che i valori sono velocità consigliate per incontrare il verde, non limiti imposti","distinguerli dai dischi bianchi con bordo rosso del limite massimo"],
    priority: 50,
    variant: 'alternate',
  }),
  asset({
    id: "Pista ciclabile (indicazione di percorso ciclabile)",
    topicId: 'information',
    labels: ["Pista ciclabile (indicazione)"],
    aliases: ["pista ciclabile","percorso ciclabile","bicicletta fondo blu"],
    useCases: ["riconoscere una pista ciclabile","distinguere indicazione da obbligo di pista ciclabile"],
    priority: 49,
    variant: 'alternate',
  }),
  asset({
    id: "Indicazione corsia riservata ai mezzi pubblici",
    topicId: 'information',
    labels: ["Corsia riservata ai mezzi pubblici"],
    aliases: ["corsia riservata autobus","corsia bus","mezzi pubblici corsia destra"],
    useCases: ["capire la riserva della corsia bus","distinguere da semplice indicazione di direzione"],
    priority: 48,
    variant: 'alternate',
  }),
  asset({
    id: "Preselezione corsie con divieto di transito agli autocarri sulla corsia di sinistra",
    topicId: 'information',
    labels: ["Preselezione corsie con divieto autocarri a sinistra"],
    aliases: ["preselezione corsie","divieto autocarri sinistra","autocarri a destra"],
    useCases: ["capire quale corsia usano gli autocarri","non leggerlo come divieto generale"],
    priority: 47,
    variant: 'alternate',
  }),
  asset({
    id: "Fermata autobus",
    topicId: 'information',
    labels: ["Fermata autobus"],
    aliases: ["fermata autobus","fermata bus","trasporto pubblico"],
    useCases: ["riconoscere la fermata bus","distinguere da corsia riservata o fermata tram"],
    priority: 46,
    variant: 'alternate',
  }),
  asset({
    id: "Posteggio per taxi",
    topicId: 'information',
    labels: ["Posteggio per taxi"],
    aliases: ["posteggio taxi","taxi arancione","sosta taxi"],
    useCases: ["riconoscere la sosta taxi","capire il divieto implicito per auto private"],
    priority: 45,
    variant: 'alternate',
  }),
  asset({
    id: "Posto telefonico di soccorso (SOS)",
    topicId: 'information',
    labels: ["Posto telefonico di soccorso (SOS)"],
    aliases: ["sos","colonnina sos","soccorso stradale","telefono di emergenza"],
    useCases: ["trovare la colonnina SOS","distinguere SOS da pronto soccorso o telefono normale"],
    priority: 44,
    variant: 'alternate',
  }),
  asset({
    id: "Posto telefonico pubblico",
    topicId: 'information',
    labels: ["Posto telefonico pubblico"],
    aliases: ["telefono pubblico","cornetta","posto telefonico"],
    useCases: ["trovare un telefono pubblico","distinguere dal pronto soccorso o polizia"],
    priority: 43,
    variant: 'alternate',
  }),
  asset({
    id: "Servizio di polizia stradale",
    topicId: 'information',
    labels: ["Servizio di polizia stradale"],
    aliases: ["polizia stradale","comando polizia","recapiti polizia"],
    useCases: ["trovare il comando di polizia stradale","capire che è solo informativo"],
    priority: 42,
    variant: 'alternate',
  }),
  asset({
    id: "Officina meccanica",
    topicId: 'information',
    labels: ["Officina meccanica"],
    aliases: ["officina","riparazioni auto","calibro a corsoio","meccanico"],
    useCases: ["trovare un'officina di riparazione","distinguere da soccorso meccanico"],
    priority: 41,
    variant: 'alternate',
  }),
  asset({
    id: "Area di servizio per autocaravan (scarico acque reflue e pulizie)",
    topicId: 'information',
    labels: ["Area di servizio per autocaravan"],
    aliases: ["area camper","scarico acque reflue","autocaravan servizio","camper service"],
    useCases: ["trovare l'area di scarico camper","distinguere da semplice parcheggio camper"],
    priority: 40,
    variant: 'alternate',
  }),
  asset({
    id: "Campeggio per tende e caravan",
    topicId: 'information',
    labels: ["Campeggio per tende e caravan"],
    aliases: ["campeggio","tende e caravan","area campeggio","marrone turistico"],
    useCases: ["trovare un campeggio","distinguere dal divieto di campeggio"],
    priority: 39,
    variant: 'alternate',
  }),
  asset({
    id: "Stazione ferroviaria con servizio di trasporto autovetture al seguito",
    topicId: 'information',
    labels: ["Stazione con trasporto auto al seguito"],
    aliases: ["treno auto al seguito","stazione trasporto auto","vagone con auto"],
    useCases: ["trovare il servizio auto-al-seguito","distinguere da semplice stazione ferroviaria"],
    priority: 38,
    variant: 'alternate',
  }),
  asset({
    id: "Servizio informazioni turistiche e sistemazione alberghiera (a 150 m)",
    topicId: 'information',
    labels: ["Informazioni turistiche e alberghi"],
    aliases: ["informazioni turistiche","simbolo i","albergo","marrone turistico"],
    useCases: ["trovare info turistiche e alloggio","distinguere marrone turistico da blu servizi"],
    priority: 37,
    variant: 'alternate',
  }),
  asset({
    id: "Indicazione di interesse turistico (fiume) con pannello toponomastico",
    topicId: 'information',
    labels: ["Interesse turistico (fiume)"],
    aliases: ["interesse turistico","fiume marrone","onde blu marrone","toponomastico fiume"],
    useCases: ["riconoscere un'indicazione turistica","non leggere lo sfondo marrone come pericolo"],
    priority: 36,
    variant: 'alternate',
  }),
  asset({
    id: "Piazzola",
    topicId: 'information',
    labels: ["Piazzola di sosta"],
    aliases: ["piazzola","piazzola di sosta","slargo","sosta laterale"],
    useCases: ["riconoscere una piazzola di sosta","distinguere da area di servizio o corsia di emergenza"],
    priority: 35,
    variant: 'alternate',
  }),
  asset({
    id: "Pannello toponomastico (località: Taranto)",
    topicId: 'information',
    labels: ["Pannello toponomastico (Taranto)"],
    aliases: ["toponomastico","inizio centro abitato","nome localita","taranto"],
    useCases: ["riconoscere l'inizio del centro abitato","distinguere da pannello direzionale"],
    priority: 34,
    variant: 'alternate',
  }),
  asset({
    id: "Fine del centro abitato (Montecompatri) con indicazione delle località successive",
    topicId: 'information',
    labels: ["Fine centro abitato (Montecompatri)"],
    aliases: ["fine centro abitato","barra rossa nome","montecompatri","uscita paese"],
    useCases: ["riconoscere la fine del centro abitato","distinguere inizio da fine (barra rossa)"],
    priority: 33,
    variant: 'alternate',
  }),
  asset({
    id: "Inizio area pedonale con eccezioni autorizzate",
    topicId: 'information',
    labels: ["Inizio area pedonale"],
    aliases: ["area pedonale","zona pedoni","eccezioni autorizzate area pedonale"],
    useCases: ["riconoscere l'area pedonale","distinguere area pedonale da ZTL"],
    priority: 32,
    variant: 'alternate',
  }),
  asset({
    id: "Inizio zona a traffico limitato (ZTL) con eccezioni",
    topicId: 'information',
    labels: ["Inizio ZTL"],
    aliases: ["ztl","zona traffico limitato","ingresso ztl","non autorizzati"],
    useCases: ["riconoscere l'ingresso in ZTL","distinguere ZTL da area pedonale"],
    priority: 31,
    variant: 'alternate',
  }),
  asset({
    id: "Delineatore di intersezione a T con doppia direzione",
    topicId: 'information',
    labels: ["Delineatore intersezione a T"],
    aliases: ["delineatore a t","intersezione a t","svolta destra o sinistra","fondo strada"],
    useCases: ["capire l'obbligo di svolta a fine strada","distinguere delineatore da obbligo di direzione"],
    priority: 30,
    variant: 'alternate',
  }),
  asset({
    id: "Delineatore speciale di curva pericolosa a destra",
    topicId: 'information',
    labels: ["Delineatore di curva pericolosa a destra"],
    aliases: ["delineatore curva","curva pericolosa destra","freccia su strisce nere"],
    useCases: ["seguire la traiettoria della curva","distinguere delineatore da obbligo di svolta"],
    priority: 29,
    variant: 'alternate',
  }),
  asset({
    id: "Pannello direzionale per veicolo operativo o ostacolo",
    topicId: 'information',
    labels: ["Pannello direzionale per veicolo operativo"],
    aliases: ["veicolo operativo","sgombraneve","freccia su strisce rosse bianche","ostacolo"],
    useCases: ["superare un veicolo operativo dal lato indicato","distinguere da obbligo direzionale"],
    priority: 28,
    variant: 'alternate',
  }),
  asset({
    id: "Pannello di chiusura corsia destra",
    topicId: 'information',
    labels: ["Chiusura corsia destra (cantiere)"],
    aliases: ["chiusura corsia destra","cantiere giallo","confluire a sinistra"],
    useCases: ["confluire quando la corsia è chiusa","riconoscere il giallo da cantiere"],
    priority: 27,
    variant: 'alternate',
  }),
  asset({
    id: "Preselezione corsie in cantiere — auto sulla corsia di sinistra, auto e autocarri sulla destra",
    topicId: 'information',
    labels: ["Preselezione corsie in cantiere"],
    aliases: ["preselezione cantiere","giallo cantiere corsie","auto sinistra autocarri destra"],
    useCases: ["distribuire i veicoli per categoria in cantiere","riconoscere indicazione temporanea gialla"],
    priority: 26,
    variant: 'alternate',
  }),
  asset({
    id: "Preavviso di deviazione per lavori — direzione Novara — lunghezza 2 km",
    topicId: 'information',
    labels: ["Preavviso di deviazione per lavori (Novara)"],
    aliases: ["deviazione lavori","preavviso deviazione","novara 2 km","cantiere giallo"],
    useCases: ["anticipare una deviazione di cantiere","riconoscere il giallo come provvisorio"],
    priority: 25,
    variant: 'alternate',
  }),
  asset({
    id: "Preavviso di deviazione obbligatoria per limite di massa",
    topicId: 'information',
    labels: ["Deviazione obbligatoria per limite di massa"],
    aliases: ["deviazione per massa","limite 7 tonnellate","deviazione mezzi pesanti","giallo deviazione"],
    useCases: ["sapere chi deve deviare per la massa","non leggere la freccia come consiglio per tutti"],
    priority: 24,
    variant: 'alternate',
  }),
  asset({
    id: "Identificativo della Strada Statale n. 2 (SS 2)",
    topicId: 'information',
    labels: ["Identificativo Strada Statale (SS 2)"],
    aliases: ["strada statale","ss 2","sigla ss","numero strada statale"],
    useCases: ["identificare una strada statale","distinguere SS da SP / SR"],
    priority: 23,
    variant: 'alternate',
  }),
  asset({
    id: "Identificativo dell'Autostrada A25",
    topicId: 'information',
    labels: ["Identificativo Autostrada (A25)"],
    aliases: ["autostrada a25","sigla a","identificativo autostrada","scudetto verde"],
    useCases: ["identificare un'autostrada","distinguere identificativo da numero di uscita"],
    priority: 22,
    variant: 'alternate',
  }),
  asset({
    id: "Identificazione strada comunale (SC 19)",
    topicId: 'information',
    labels: ["Identificativo Strada Comunale (SC 19)"],
    aliases: ["strada comunale","sc 19","sigla sc","identificativo comunale"],
    useCases: ["identificare una strada comunale","distinguere SC da SS / SP / SR"],
    priority: 21,
    variant: 'alternate',
  }),
  asset({
    id: "Itinerario europeo (strada europea E55)",
    topicId: 'information',
    labels: ["Itinerario europeo (E55)"],
    aliases: ["itinerario europeo","e55","strada europea","sigla e verde"],
    useCases: ["identificare un itinerario europeo","distinguere E da A o SS"],
    priority: 20,
    variant: 'alternate',
  }),
  asset({
    id: "Segnale di conferma autostradale — distanze per Firenze, Bologna, Milano",
    topicId: 'information',
    labels: ["Segnale chilometrico autostradale"],
    aliases: ["segnale chilometrico","distanze km","firenze bologna milano","km residui"],
    useCases: ["leggere le distanze residue","associare il colore di fondo al tipo di strada"],
    priority: 19,
    variant: 'alternate',
  }),
  asset({
    id: "Uscita di emergenza",
    topicId: 'information',
    labels: ["Uscita di emergenza"],
    aliases: ["uscita di emergenza","uscita di sicurezza","verde salvataggio","via di fuga"],
    useCases: ["trovare l'uscita di emergenza in galleria","capire che non riguarda la viabilità ordinaria"],
    priority: 18,
    variant: 'alternate',
  }),
  asset({
    id: "Estintore",
    topicId: 'information',
    labels: ["Estintore"],
    aliases: ["estintore","antincendio","rosso estintore","attrezzatura incendio"],
    useCases: ["localizzare un estintore","distinguere da pericolo di incendio"],
    priority: 17,
    variant: 'alternate',
  }),
  asset({
    id: "Confronto: rotatoria obbligatoria (A) e preavviso di rotatoria (B)",
    topicId: 'information',
    labels: ["Rotatoria obbligatoria vs preavviso di rotatoria"],
    aliases: ["rotatoria obbligatoria","preavviso rotatoria","cerchio blu vs triangolo rotatoria"],
    useCases: ["distinguere obbligo di rotatoria dal preavviso","capire dove si trova ciascun segnale"],
    priority: 16,
    variant: 'alternate',
  }),
  asset({
    id: "Schema: strettoia simmetrica (A) e diritto di precedenza nei sensi unici alternati (B)",
    topicId: 'information',
    labels: ["Strettoia simmetrica e precedenza nei sensi unici alternati"],
    aliases: ["strettoia simmetrica","sensi unici alternati","diritto di precedenza","frecce contrapposte colori"],
    useCases: ["capire chi ha la precedenza nella strettoia","distinguere da doppio senso di circolazione"],
    priority: 15,
    variant: 'alternate',
  }),
  asset({
    id: "Segnale di valico alpino — Stelvio: stato di transito e obbligo catene",
    topicId: 'information',
    labels: ["Valico alpino Stelvio (aperto)"],
    aliases: ["valico stelvio","passo aperto","obbligo catene valico","fascia verde aperto"],
    useCases: ["capire se il valico è transitabile","distinguere il pannello composito da obbligo catene"],
    priority: 14,
    variant: 'alternate',
  }),
  asset({
    id: "Segnale di valico alpino — Stelvio: passo chiuso fino a Trafoi",
    topicId: 'information',
    labels: ["Valico alpino Stelvio (chiuso)"],
    aliases: ["valico stelvio chiuso","passo chiuso","fascia rossa chiuso","trafoi"],
    useCases: ["sapere che il valico è chiuso","distinguere versione chiuso da aperto"],
    priority: 13,
    variant: 'alternate',
  }),
  asset({
    id: "Pannello arancione di trasporto merci pericolose (codice Kemler / numero ONU)",
    topicId: 'information',
    labels: ["Pannello Kemler (merci pericolose)"],
    aliases: ["pannello kemler","merci pericolose","codice onu","pannello arancione adr"],
    useCases: ["riconoscere un veicolo con merci pericolose","capire che è applicato al veicolo, non a bordo strada"],
    priority: 12,
    variant: 'alternate',
  }),
  // ── traffic_lights (13) — P8 Step 4 ──
  asset({
    id: "Semaforo veicolare a tre luci",
    topicId: 'traffic_lights',
    labels: ["semaforo veicolare","tre luci"],
    aliases: ["semaforo","rosso giallo verde","luce gialla","linea di arresto","semaforo veicolare"],
    useCases: ["spiegare il significato delle tre luci del semaforo ordinario","chiarire la regola del giallo (fermarsi se possibile)","ricordare che con il verde non c'e precedenza assoluta sui pedoni"],
    priority: 100,
    variant: 'primary',
  }),
  asset({
    id: "Schema dei semafori a luci direzionali",
    topicId: 'traffic_lights',
    labels: ["luci direzionali","frecce semaforiche"],
    aliases: ["semaforo a frecce","luci direzionali","svolta a destra","freccia verde","corsia di svolta"],
    useCases: ["spiegare che ogni semaforo a frecce regola solo la corsia o direzione indicata","distinguere le luci direzionali dal semaforo dell'intero incrocio","ricordare la precedenza ai pedoni anche con verde direzionale"],
    priority: 95,
    variant: 'primary',
  }),
  asset({
    id: "Lanterna semaforica per pedoni",
    topicId: 'traffic_lights',
    labels: ["semaforo pedonale","lanterna pedoni"],
    aliases: ["semaforo pedoni","omino rosso","omino verde","attraversamento pedonale","sagoma pedone"],
    useCases: ["distinguere il semaforo pedonale da quello veicolare","spiegare rosso pedone fermo / verde pedone in cammino","ricordare la precedenza ai pedoni che hanno gia iniziato la traversata"],
    priority: 90,
    variant: 'primary',
  }),
  asset({
    id: "Lanterna semaforica per velocipedi",
    topicId: 'traffic_lights',
    labels: ["semaforo ciclisti","lanterna velocipedi"],
    aliases: ["semaforo bici","lanterna velocipedi","bicicletta rossa","attraversamento ciclabile","ciclisti"],
    useCases: ["spiegare che la lanterna con bicicletta vincola solo i velocipedi","distinguerla dalle lanterne generali per tutti i veicoli","chiarire che e un dispositivo luminoso, non un cartello informativo"],
    priority: 85,
    variant: 'primary',
  }),
  asset({
    id: "Vigile con braccia aperte (segnalazione manuale di arresto)",
    topicId: 'traffic_lights',
    labels: ["vigile braccia aperte","segnalazione manuale"],
    aliases: ["vigile braccia distese","agente del traffico","fermarsi vigile","petto schiena vigile","segnalazione manuale"],
    useCases: ["spiegare che il gesto del vigile prevale su semafori e segnali","chiarire chi si ferma (petto/schiena) e chi procede (fianchi)","evitare l'errore di credere che braccia aperte = via libera da ogni direzione"],
    priority: 80,
    variant: 'primary',
  }),
  asset({
    id: "Schema dei segnali luminosi per veicoli su rotaia (tram)",
    topicId: 'traffic_lights',
    labels: ["semaforo tram","trasporto pubblico"],
    aliases: ["semaforo tram","veicoli su rotaia","barretta triangolo","trasporto pubblico di linea","autobus di linea"],
    useCases: ["spiegare le barrette/triangolo equivalenti a rosso-giallo-verde","chiarire che vale per tutto il trasporto pubblico di linea, non solo tram","distinguerlo dal semaforo veicolare ordinario e da quello a corsie reversibili"],
    priority: 75,
    variant: 'primary',
  }),
  asset({
    id: "Schema dei segnali luminosi di corsia (corsie reversibili)",
    topicId: 'traffic_lights',
    labels: ["corsie reversibili","segnali di corsia"],
    aliases: ["croce rossa corsia","freccia verde corsia","freccia gialla obliqua","corsie reversibili","corsia chiusa"],
    useCases: ["spiegare croce rossa / freccia verde / freccia gialla sopra la corsia","chiarire che la freccia gialla obliqua impone di cambiare corsia","distinguere i pannelli di corsia dai semafori d'incrocio"],
    priority: 70,
    variant: 'primary',
  }),
  asset({
    id: "Spia di anomalia dell'impianto frenante / freno di stazionamento",
    topicId: 'traffic_lights',
    labels: ["spia freni","freno a mano"],
    aliases: ["spia freni rossa","freno di stazionamento","liquido freni","punto esclamativo parentesi","anomalia frenante"],
    useCases: ["spiegare le tre cause della spia rossa dei freni","distinguerla dalla spia ABS","ricordare di disinserire il freno a mano prima di partire"],
    priority: 50,
    variant: 'alternate',
  }),
  asset({
    id: "Spia della pressione dell'olio motore",
    topicId: 'traffic_lights',
    labels: ["spia olio","pressione olio"],
    aliases: ["spia olio rossa","oliatore con goccia","pressione olio","lubrificazione motore","fermarsi subito"],
    useCases: ["spiegare che segnala anomalia immediata della pressione, non il cambio olio","distinguerla dalla spia temperatura e dalla spia carburante","ricordare di fermarsi appena possibile"],
    priority: 45,
    variant: 'alternate',
  }),
  asset({
    id: "Spia di temperatura del liquido di raffreddamento del motore",
    topicId: 'traffic_lights',
    labels: ["spia temperatura","liquido raffreddamento"],
    aliases: ["spia temperatura rossa","termometro liquido","surriscaldamento motore","liquido refrigerante","radiatore"],
    useCases: ["spiegare che il rosso fisso impone fermata immediata","distinguerla dalla spia dell'olio motore","ricordare il rischio di fondere il motore"],
    priority: 40,
    variant: 'alternate',
  }),
  asset({
    id: "Spia di mancato allacciamento delle cinture di sicurezza",
    topicId: 'traffic_lights',
    labels: ["spia cinture","cintura sicurezza"],
    aliases: ["spia cinture rossa","cintura non allacciata","sagoma seduta","obbligo cinture","occupanti veicolo"],
    useCases: ["spiegare che segnala cinture non allacciate (anteriori e posteriori)","distinguerla dalla spia airbag e dal sistema di ritenuta bambini","ricordare l'obbligo di legge delle cinture"],
    priority: 35,
    variant: 'alternate',
  }),
  asset({
    id: "Spia abbaglianti accesi",
    topicId: 'traffic_lights',
    labels: ["spia abbaglianti","luci abbaglianti"],
    aliases: ["spia abbaglianti blu","raggi orizzontali","luci abbaglianti","fuori centri abitati","fascio alto"],
    useCases: ["spiegare che indica gli abbaglianti attivi (blu sul quadro)","distinguerla dalla spia anabbaglianti (raggi inclinati in basso)","ricordare di spegnerli in caso di incrocio o veicolo che precede"],
    priority: 30,
    variant: 'alternate',
  }),
  asset({
    id: "Spia delle luci abbaglianti",
    topicId: 'traffic_lights',
    labels: ["spia abbaglianti","proiettore abbaglianti"],
    aliases: ["spia abbaglianti blu","faro fascio sinistra","luci abbaglianti","raggi paralleli","150 metri"],
    useCases: ["identificare il pittogramma standard degli abbaglianti","distinguerlo dalla spia anabbaglianti (raggi verso il basso)","ricordare lo spegnimento entro 150 metri da un veicolo che precede"],
    priority: 25,
    variant: 'alternate',
  }),
  // ── road_markings (7) — P8 Step 4 ──
  asset({
    id: "Strisce longitudinali continue",
    topicId: 'road_markings',
    labels: ["Strisce longitudinali continue","Linea bianca continua"],
    // "mezzeria" dropped from the identity: the crop shows continuous
    // longitudinal lines with no direction-of-travel context to prove a centre line.
    aliases: ["linea continua","striscia continua","strisce longitudinali continue","sorpasso vietato","non si supera"],
    useCases: ["spiegare che la striscia continua non si oltrepassa","distinguere la continua dalla discontinua","riconoscerla nei tratti a scarsa visibilità (curve, dossi, gallerie)"],
    priority: 100,
    variant: 'primary',
  }),
  asset({
    id: "Linea di mezzeria discontinua (separazione dei sensi di marcia)",
    topicId: 'road_markings',
    labels: ["Linea di mezzeria discontinua","Linea tratteggiata"],
    aliases: ["linea discontinua","striscia tratteggiata","mezzeria discontinua","sorpasso consentito","linee a tratti"],
    useCases: ["spiegare che la linea tratteggiata permette sorpasso e invasione temporanea","distinguerla dalla continua","introdurre la doppia linea (continua + discontinua)"],
    priority: 95,
    variant: 'primary',
  }),
  asset({
    id: "Attraversamento pedonale (segnaletica orizzontale - strisce zebrate)",
    topicId: 'road_markings',
    labels: ["Attraversamento pedonale","Strisce pedonali (zebrate)"],
    aliases: ["strisce pedonali","attraversamento pedonale","zebre","strisce zebrate","passaggio pedoni"],
    useCases: ["riconoscere l'attraversamento pedonale e l'obbligo di fermarsi per i pedoni","distinguere le strisce zebrate dalla linea di arresto","spiegare la validità dentro e fuori dei centri abitati"],
    priority: 90,
    variant: 'primary',
  }),
  asset({
    id: "Schema di intersezione con striscia trasversale continua di arresto",
    topicId: 'road_markings',
    labels: ["Striscia trasversale continua di arresto","Linea di arresto all'intersezione"],
    aliases: ["striscia trasversale continua","linea di arresto","striscia di arresto","intersezione a t","incrocio a t"],
    useCases: ["riconoscere il punto prima del quale fermarsi quando l'arresto è richiesto","distinguere la linea trasversale di arresto dalla linea longitudinale","non dedurre precedenze da segnali non rappresentati nello schema"],
    priority: 85,
    variant: 'primary',
  }),
  asset({
    id: "Strisce gialle e nere di divieto di sosta (segnaletica orizzontale)",
    topicId: 'road_markings',
    labels: ["Strisce gialle e nere (divieto di sosta)","Cordolo giallo-nero"],
    aliases: ["strisce gialle e nere","cordolo giallo nero","divieto di sosta orizzontale","bordo marciapiede dipinto","giallo nero"],
    useCases: ["spiegare che il binomio giallo-nero vieta la sosta anche senza segnale verticale","distinguerle dalle strisce gialle continue e dalle bianco-rosse","chiarire che la fermata breve resta consentita"],
    priority: 60,
    variant: 'alternate',
  }),
  asset({
    id: "Fermata autobus — segnaletica orizzontale a zigzag gialla",
    topicId: 'road_markings',
    labels: ["Fermata autobus (zigzag gialla)","Zigzag gialla BUS"],
    aliases: ["fermata autobus","zigzag gialla","linee a dente di sega","bus","divieto di sosta bus"],
    useCases: ["riconoscere l'area di fermata autobus dalle linee gialle a zigzag","chiarire che vieta la sosta ma NON la fermata","distinguerla da una zona di sosta libera o corsia preferenziale"],
    priority: 55,
    variant: 'alternate',
  }),
  asset({
    id: "Stallo di sosta riservato — segnaletica orizzontale",
    topicId: 'road_markings',
    labels: ["Stallo di sosta riservato","Stallo con P o X"],
    aliases: ["stallo riservato","sosta riservata","stallo con x","parcheggio invalidi","riquadro di sosta"],
    useCases: ["interpretare il riquadro come stallo riservato a categorie aventi diritto","spiegare il significato della P e della grande X (riserva o inutilizzabilità temporanea)","distinguerlo da uno stallo di sosta libero"],
    priority: 50,
    variant: 'alternate',
  }),
  // ── temporary_signs (13) — P8 Step 4 ──
  asset({
    id: "Sbarramento di lavori in corso o di chiusura strada",
    topicId: 'temporary_signs',
    labels: ["Sbarramento lavori in corso","Chiusura strada"],
    aliases: ["sbarramento","chiusura strada","barriera bianco rosso","lavori in corso","strisce diagonali rosse"],
    useCases: ["spiegare la barriera di chiusura totale della carreggiata in zona di cantiere","distinguere lo sbarramento dai delineatori di curva e dagli ostacoli","ricordare che le deviazioni segnalate vanno seguite tassativamente"],
    priority: 100,
    variant: 'primary',
  }),
  asset({
    id: "Cono di delineamento",
    topicId: 'temporary_signs',
    labels: ["Cono di delineamento","Cono stradale"],
    aliases: ["cono","cono stradale","rosso bianco","delimitazione temporanea","transenna leggera"],
    useCases: ["spiegare il cono come dispositivo temporaneo di delimitazione di cantieri e incidenti","chiarire che non è un segnale di prescrizione ma va sempre rispettato","distinguere il cono dai delineatori di margine fissi"],
    priority: 95,
    variant: 'primary',
  }),
  asset({
    id: "Delineatore per strade di montagna soggette ad alto innevamento",
    topicId: 'temporary_signs',
    labels: ["Delineatore di ostacolo","Paletto giallo-nero"],
    aliases: ["paletto giallo nero","delineatore ostacolo","bande gialle","ostacolo laterale","restringimento"],
    useCases: ["spiegare il paletto giallo-nero che evidenzia un ostacolo puntuale o un'area di lavori","distinguerlo dai delineatori di curva bianco-neri","evitare la confusione con il divieto di sosta"],
    priority: 90,
    variant: 'primary',
  }),
  asset({
    id: "Preavviso di corsia chiusa per cantiere a 500 m",
    topicId: 'temporary_signs',
    labels: ["Corsia chiusa per cantiere","Preavviso lavori 500 m"],
    aliases: ["corsia chiusa","cantiere 500 m","pannello giallo frecce","deviazione corsia","lavori in corso preavviso"],
    useCases: ["spiegare il pannello giallo di preavviso di chiusura corsia in approccio al cantiere","chiarire che lo sfondo giallo è segnaletica temporanea che prevale sulla permanente","ricordare di preparare gradualmente il cambio corsia"],
    priority: 85,
    variant: 'primary',
  }),
  asset({
    id: "Delineatori normali di margine",
    topicId: 'temporary_signs',
    labels: ["Delineatori di margine","Delineatori margine carreggiata"],
    aliases: ["delineatori margine","rosso a destra","bianco a sinistra","bordo carreggiata","scarsa visibilità"],
    useCases: ["spiegare la convenzione cromatica rosso a destra e bianco a sinistra","chiarire che sono dispositivi di sicurezza, non segnali di prescrizione","distinguerli dalle pietre miliari e dai delineatori di curva"],
    priority: 80,
    variant: 'primary',
  }),
  asset({
    id: "Pannello segnaletico per carico sporgente",
    topicId: 'temporary_signs',
    labels: ["Carico sporgente","Pannello carico sporgente"],
    aliases: ["carico sporgente","pannello quadrato bianco rosso","catarifrangenti angoli","sporgenza posteriore","obbligo veicolo"],
    useCases: ["spiegare il pannello obbligatorio quadrato da esporre per carico che sporge posteriormente","distinguerlo dalla segnaletica di cantiere a strisce bianche e rosse","ricordare che la sua mancanza è sanzione amministrativa"],
    priority: 75,
    variant: 'primary',
  }),
  asset({
    id: "Delineatori normali di margine (sinistro a due catadiottri, destro a uno)",
    topicId: 'temporary_signs',
    labels: ["Delineatori area di sosta riservata","Delineatori speciali gialli"],
    aliases: ["area sosta riservata","delineatori gialli","dischi gialli","area emergenza","catarifrangenti gialli"],
    useCases: ["spiegare i delineatori gialli che evidenziano un'area di sosta riservata o di emergenza","distinguerli dai delineatori normali di margine bianchi e rossi","chiarire che la sosta può essere consentita solo a certe categorie"],
    priority: 70,
    variant: 'primary',
  }),
  asset({
    id: "Pannello posteriore retroriflettente per autoveicoli merci oltre 3,5 t",
    topicId: 'temporary_signs',
    labels: ["Pannello posteriore autocarri oltre 3,5 t"],
    // "veicolo lento" dropped: the slow-vehicle marking is a different device.
    aliases: ["pannello posteriore autocarro","bande giallo rosso oblique","pannello retroriflettente","mezzo pesante alta visibilità"],
    useCases: ["spiegare il pannello retroriflettente degli autoveicoli merci oltre 3,5 t","distinguerlo dal pannello per carichi sporgenti e dai delineatori di curva","chiarire che indica un mezzo in marcia, non un ostacolo fermo"],
    priority: 65,
    variant: 'primary',
  }),
  asset({
    id: "Pannelli distanziometrici di passaggio a livello e croce di Sant'Andrea",
    topicId: 'temporary_signs',
    labels: ["Distanziometrici passaggio a livello","Croce di Sant'Andrea"],
    aliases: ["croce di sant'andrea","passaggio a livello","bande distanza","binario unico","150 100 50 metri"],
    useCases: ["spiegare i pannelli distanziometrici in avvicinamento al passaggio a livello","chiarire che più bande indicano maggiore distanza residua","distinguere croce singola (binario unico) da croce doppia (più binari)"],
    priority: 55,
    variant: 'alternate',
  }),
  asset({
    id: "Cartello di passo carrabile (autorizzazione comunale)",
    topicId: 'temporary_signs',
    labels: ["Passo carrabile","Cartello passo carrabile"],
    aliases: ["passo carrabile","autorizzazione comunale","accesso carrabile","divieto sosta ingresso","cancello"],
    useCases: ["spiegare il cartello di passo carrabile come autorizzazione comunale di un singolo accesso","distinguerlo dal comune segnale di divieto di sosta","chiarire che il divieto di sosta davanti all'accesso è assoluto"],
    priority: 50,
    variant: 'alternate',
  }),
  asset({
    id: "Pannello retroriflettente di sagoma posteriore (per veicoli pesanti / rimorchi lunghi)",
    topicId: 'temporary_signs',
    labels: ["Sagoma posteriore veicoli pesanti","Pannello retroriflettente rimorchi"],
    aliases: ["sagoma posteriore","veicoli pesanti","rimorchi lunghi","cornice rossa sfondo giallo","retroriflettente"],
    useCases: ["spiegare il pannello di sagoma posteriore per rendere visibili veicoli pesanti e rimorchi di notte","distinguerlo dal codice Kemler e dal pannello trasporti eccezionali","chiarire che è un dispositivo del veicolo, non un segnale stradale"],
    priority: 45,
    variant: 'alternate',
  }),
  asset({
    id: "Pannello integrativo di continuazione, disposizione orizzontale",
    topicId: 'information',
    labels: ["Pannello integrativo di continuazione (orizzontale)"],
    // Dashboard vocabulary dropped: this is a road panel, not a car control.
    aliases: ["pannello di continuazione","continuazione orizzontale","frecce contrapposte su pannello","estensione della prescrizione"],
    useCases: ["spiegare che la prescrizione del segnale sovrastante prosegue nelle direzioni indicate dalle frecce","distinguerlo dai pannelli di inizio e di fine","chiarire che non è la spia degli indicatori di direzione del cruscotto"],
    priority: 40,
    variant: 'alternate',
  }),
  asset({
    id: "Spia/comando della segnalazione luminosa di pericolo (4 frecce)",
    topicId: 'temporary_signs',
    labels: ["Spia quattro frecce","Segnalazione luminosa di pericolo"],
    aliases: ["quattro frecce","luci di emergenza","spia triangolo","segnalazione pericolo","comando cruscotto"],
    useCases: ["spiegare il simbolo a triangolo delle quattro frecce sul cruscotto","distinguerlo dalla spia degli indicatori di direzione","non confondere il comando dell'auto con il triangolo di emergenza da posare a terra"],
    priority: 35,
    variant: 'alternate',
  }),
];

const TOPIC_ALIASES: Record<QwenVoiceVisualTopicId, string[]> = {
  speed_limits: [
    'limite',
    'limiti',
    'velocita',
    'autostrada',
    'km h',
    'centotrenta',
    'cento dieci',
    '130',
    '110',
  ],
  overtaking: ['sorpasso', 'sorpassare', 'sorpassi', 'superare', 'autocarri', 'mezzi pesanti'],
  right_of_way: ['precedenza', 'dare precedenza', 'diritto di precedenza', 'incrocio', 'incroci', 'stop'],
  warning_danger: ['pericolo', 'pericoloso', 'curva', 'curve', 'sdrucciolevole', 'strada deformata', 'dosso', 'cunetta', 'bagnato', 'pioggia', 'aderenza', 'frenata', 'frenare'],
  stopping_parking: ['sosta', 'sostare', 'fermata', 'fermarti', 'parcheggio', 'parcheggiare'],
  safety_distance: ['distanza di sicurezza', 'distanze di sicurezza', 'tamponamento', 'distanziometrici'],
  mandatory: ["obbligo","segnale di obbligo","cerchio blu","obbligatorio","direzione obbligatoria","devo","segnali di obbligo","fondo blu obbligo","velocità minima","catene obbligatorie"],
  prohibitions: ["divieto","segnale di divieto","vietato","cerchio rosso","senso vietato","divieto di transito","divieto di accesso","transito vietato","fine divieti"],
  information: ["indicazione","segnale di indicazione","segnale blu","direzione","informazione","dove si va","localita","preavviso di direzione","cartello blu","indicazioni stradali"],
  traffic_lights: ["semaforo","semaforico","luci","luce rossa","luce verde","luce gialla","lanterna semaforica","vigile","segnalazione luminosa","spia cruscotto"],
  road_markings: ["segnaletica orizzontale","strisce","linea continua","linea discontinua","righe per terra","segni sull'asfalto","corsia","mezzeria","strisce pedonali","righe bianche"],
  temporary_signs: ["segnali complementari","segnali temporanei","cantiere","lavori in corso","segnale giallo","temporaneo","delineatore","deviazione provvisoria","sbarramento strada","cono stradale"],
  unknown: [],
};

export function normalizeQwenVoiceVisualText(text: string): string {
  return text
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/Ã /g, 'a')
    .replace(/Ã¨/g, 'e')
    .replace(/Ã©/g, 'e')
    .replace(/Ã¬/g, 'i')
    .replace(/Ã²/g, 'o')
    .replace(/Ã¹/g, 'u')
    .replace(/[—–-]+/g, ' ')
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

export function getQwenVoiceVisualTopicAliases(topicId: QwenVoiceVisualTopicId): string[] {
  return TOPIC_ALIASES[topicId] ?? [];
}

const TOPIC_PRIORITY: QwenVoiceVisualTopicId[] = [
  'right_of_way',
  'overtaking',
  'stopping_parking',
  'safety_distance',
  'warning_danger',
  'speed_limits',
  'mandatory',
  'prohibitions',
  'information',
  'traffic_lights',
  'road_markings',
  'temporary_signs',
];

function scoreVisualTopicText(text: string, topicId: QwenVoiceVisualTopicId): number {
  const normalized = normalizeQwenVoiceVisualText(text);
  if (!normalized) return 0;
  let score = 0;
  for (const alias of getQwenVoiceVisualTopicAliases(topicId)) {
    const normalizedAlias = normalizeQwenVoiceVisualText(alias);
    if (!normalizedAlias) continue;
    const re = new RegExp(`\\b${normalizedAlias.replace(/\s+/g, '\\s+')}\\b`, 'i');
    if (re.test(normalized)) score += normalizedAlias.includes(' ') ? 3 : 1;
  }
  return score;
}

export function detectQwenVoiceVisualTopicFromText(text: string): {
  topicId: QwenVoiceVisualTopicId;
  confidence: 'high' | 'medium' | 'low';
  score: number;
} {
  const scores = TOPIC_PRIORITY
    .map((topicId) => ({ topicId, score: scoreVisualTopicText(text, topicId) }))
    .filter((item) => item.score > 0)
    .sort((a, b) => b.score - a.score || TOPIC_PRIORITY.indexOf(a.topicId) - TOPIC_PRIORITY.indexOf(b.topicId));
  const best = scores[0];
  if (!best) return { topicId: 'unknown', confidence: 'low', score: 0 };
  return {
    topicId: best.topicId,
    confidence: best.score >= 3 ? 'high' : 'medium',
    score: best.score,
  };
}

export function getQwenVoiceVisualAssetsForTopic(
  topicId: QwenVoiceVisualTopicId,
  options: { variant?: 'primary' | 'alternate'; excludeIds?: string[] } = {},
): QwenVoiceVisualAsset[] {
  const excluded = new Set((options.excludeIds ?? []).map(normalizeQwenVoiceVisualText));
  return QWEN_VOICE_VISUAL_ASSETS
    .filter((asset) => asset.topicId === topicId)
    .filter((asset) => !options.variant || asset.variant === options.variant)
    .filter((asset) => !excluded.has(normalizeQwenVoiceVisualText(asset.id)))
    .sort((a, b) => b.priority - a.priority);
}

export function getQwenVoiceVisualTopicForImageId(imageId: string): QwenVoiceVisualTopicId {
  const normalized = normalizeQwenVoiceVisualText(imageId);
  // P8 Step 1: exact normalized-id match FIRST. Callers pass a rendered sign's id
  // (italianName), so the asset whose id matches exactly is always the correct topic.
  // This avoids substring collisions in the expanded 100+ sign registry (e.g.
  // "Pannelli distanziometrici (tre, due, una banda)" vs the passaggio-a-livello
  // distanziometrici, or shared "Croce di Sant'Andrea" prefixes across families).
  const exact = QWEN_VOICE_VISUAL_ASSETS.find(
    (asset) => normalizeQwenVoiceVisualText(asset.id) === normalized,
  );
  if (exact) return exact.topicId;
  const match = QWEN_VOICE_VISUAL_ASSETS.find((asset) => {
    const assetNames = [asset.id, ...asset.labels, ...asset.aliases].map(normalizeQwenVoiceVisualText);
    return assetNames.some((name) => normalized === name || normalized.includes(name) || name.includes(normalized));
  });
  return match?.topicId ?? 'unknown';
}

export function markerNameFromQwenVoiceVisualMarker(marker: string): string {
  return marker.replace(/^\[\[image:sign:/i, '').replace(/\]\]$/, '');
}
